import type {
  AiAssessmentView,
  FactSkillGroupKey,
  FactSkillItemView,
  FactSkillMemberView,
  FactSkillView,
  PowerGroupKey,
  PowerMemberView,
  PowersView,
  TeamMatchView,
} from "./match-result-codec";
import { levelFromPercent, type SkillLevel } from "@/lib/skill-levels";
import { POWERS_WEIGHT_DISPLAY_DIVISOR } from "./match-design-rules";

export interface ResultPerson {
  id: string;
  name: string;
  profession?: string | null;
  avatarContent?: string | null;
  avatarMime?: string | null;
}

export interface DemographicsView {
  dept: string | null;
  gender: string | null;
  age: string | null;
}

export function buildDemographics(tm: TeamMatchView | null): DemographicsView {
  if (!tm) return { dept: null, gender: null, age: null };
  // Genesis buckets Persons with no gender set as "diverse". If the team has
  // zero declared m/f and only "d", treat that as "no data" and hide the chip
  // instead of misleadingly showing all-diverse.
  const declaredGender = tm.maleCount > 0 || tm.femaleCount > 0;
  const parts: string[] = [];
  if (tm.maleCount > 0) parts.push(`${tm.maleCount}m`);
  if (tm.femaleCount > 0) parts.push(`${tm.femaleCount}f`);
  if (tm.diverseCount > 0 && declaredGender) parts.push(`${tm.diverseCount}d`);
  const gender = parts.length ? parts.join("/") : null;

  let age: string | null = null;
  // Hide age entirely when no Person has a birth date filled – otherwise the
  // backend returns 0/0 and we end up displaying "0 yrs" which looks broken.
  const ageMissing = tm.minAge === 0 && tm.maxAge === 0;
  if (tm.minAge !== null && tm.maxAge !== null && !ageMissing) {
    if (tm.minAge === tm.maxAge) {
      age = `${tm.minAge} yrs`;
    } else if (tm.averageAge !== null) {
      age = `${tm.minAge}–${tm.maxAge} yrs (avg ${tm.averageAge.toFixed(1)})`;
    } else {
      age = `${tm.minAge}–${tm.maxAge} yrs`;
    }
  }

  const dept = tm.mostFrequentDepartmentCount > 0 ? `${tm.mostFrequentDepartmentCount}` : null;
  return { dept, gender, age };
}

export interface PowerClusterEntry {
  key: PowerGroupKey;
  // null = kein Wert zeichenbar: entweder hat niemand ein Element dieses Clusters eingeschaetzt,
  // oder es haengt an einer einzigen Person (siehe MEAN_MIN_MEMBERS).
  score: number | null;
  // Wie viele Personen ueberhaupt eine Einschaetzung in diesem Cluster haben.
  assessedMembers: number;
}

// Ab so vielen bewerteten Personen ist ein Mittel eine Aussage ueber das Team. Darunter ist es
// die eine Person mit dem Etikett des Teams. Gilt fuer jede Flaeche, die das Mittel OHNE seinen
// Nenner zeigt - also fuer beide Radare. Listen, die "1 von 1" danebenschreiben, brauchen die
// Grenze nicht: dort steht die duenne Datenlage schon da.
// ponytail: eine Zahl, kein Konfidenzmodell; hochsetzen, falls 2 zu duenn wirkt.
export const MEAN_MIN_MEMBERS = 2;

const CLUSTER_ORDER: PowerGroupKey[] = [
  "TEAM_SPIRIT",
  "COMMUNICATION",
  "RESILIENCE",
  "RESULT_ORIENTATION",
  "FUTURE_ORIENTATION",
  "INVENTIVENESS",
];

export function buildPowerClusters(powers: PowersView | null): PowerClusterEntry[] {
  if (!powers) return CLUSTER_ORDER.map((key) => ({ key, score: null, assessedMembers: 0 }));
  const groupOf = new Map(powers.criteria.map((c) => [c.element, c.group]));
  return CLUSTER_ORDER.map((key) => {
    const assessedMembers = powers.teamMembers.filter((m) =>
      m.items.some((i) => groupOf.get(i.criterion.element) === key && i.value != null),
    ).length;
    const mean = powers.groupPercent[key] ?? null;
    return {
      key,
      score: assessedMembers >= MEAN_MIN_MEMBERS ? mean : null,
      assessedMembers,
    };
  });
}

export interface PowersCoverage {
  // Personen im Team, von denen ueberhaupt eine Power-Einschaetzung vorliegt.
  measured: number;
  total: number;
}

// Die eine Zahl, die alle Powers-Kaesten teilen: ueber wie viele Personen ihre Schnitte,
// Zaehlungen und Formen ueberhaupt laufen. Ohne sie wandern die Nenner still mit der
// Datenlage (32/40 wird 28/32), ohne dass irgendwo steht, warum.
export function buildPowersCoverage(powers: PowersView | null): PowersCoverage {
  if (!powers) return { measured: 0, total: 0 };
  return {
    measured: powers.teamMembers.filter((m) => m.items.some((i) => i.value != null)).length,
    total: powers.teamMembers.length,
  };
}

export interface HeatmapCell {
  element: string;
  group: string;
  value: number | null;
}

export interface HeatmapRow {
  personId: string;
  personName: string;
  // null = diese Person hat keine einzige eingeschaetzte Power.
  totalPercent: number | null;
  cells: HeatmapCell[];
}

export function buildHeatmapRows(
  powers: PowersView | null,
  persons: ResultPerson[],
): HeatmapRow[] {
  if (!powers) return [];
  const elements = powers.criteria.map((c) => ({ element: c.element, group: c.group }));
  const personById = new Map(persons.map((p) => [p.id, p]));
  return powers.teamMembers
    .map((member) => {
      const personName = personById.get(member.personId)?.name ?? member.personId;
      const cells: HeatmapCell[] = elements.map((e) => {
        const found = member.items.find((i) => i.criterion.element === e.element);
        return { element: e.element, group: e.group, value: found?.value ?? null };
      });
      return {
        personId: member.personId,
        personName,
        // Von genesis geliefertes member.totalPercent ist mit #160 entfernt; aus den
        // per-Element-Atomen abgeleitet (@adr SPARK-0030-match-result-scoring-atom-derived@1 §2).
        totalPercent: memberPowerScore(member),
        cells,
      };
    })
    // Unbewertete ans Ende, aber als eigener Block - nicht unter die schwaechste Person
    // einsortiert, wo sie wie das schlechteste Teammitglied gelesen wuerden.
    .sort((a, b) => {
      if (a.totalPercent == null || b.totalPercent == null) {
        return (a.totalPercent == null ? 1 : 0) - (b.totalPercent == null ? 1 : 0);
      }
      return b.totalPercent - a.totalPercent;
    });
}

export interface PowerColumnGroup {
  key: PowerGroupKey;
  elements: string[];
}

// Spaltengruppen der Powers-Matrix: die sechs Cluster in fester Reihenfolge, jeweils
// nur mit den Elementen, die genesis tatsaechlich geliefert hat. Treibt die
// faltbaren Cluster-Spalten in TeamPowersBody.
export function buildPowerColumnGroups(powers: PowersView | null): PowerColumnGroup[] {
  if (!powers) return [];
  return CLUSTER_ORDER.map((key) => ({
    key,
    elements: powers.criteria.filter((c) => c.group === key).map((c) => c.element),
  })).filter((g) => g.elements.length > 0);
}

// Cluster-Wert einer Person: Mittel der vorhandenen Element-Werte dieses Clusters.
// null, wenn die Person in diesem Cluster nichts bewertet hat.
export function clusterMean(cells: HeatmapCell[], group: string): number | null {
  const values = cells.filter((c) => c.group === group && c.value !== null).map((c) => c.value as number);
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}


export interface RequirementColumn {
  label: string;
  rawName: string;
}

export interface RequirementRow {
  personId: string;
  personName: string;
  score: number;
  cells: Array<{ label: string; matched: boolean }>;
}

function conditionName(condition: unknown): string {
  if (condition && typeof condition === "object" && "name" in condition) {
    const v = (condition as { name?: unknown }).name;
    if (typeof v === "string") return v;
  }
  return "";
}

export function buildElementRequirementRows(
  factSkill: FactSkillView | null,
  persons: ResultPerson[],
): { columns: RequirementColumn[]; rows: RequirementRow[] } {
  if (!factSkill) return { columns: [], rows: [] };
  const powerCriteria = factSkill.criteria.filter((c) => c.group === "POWERS");
  if (powerCriteria.length === 0) return { columns: [], rows: [] };
  const columns: RequirementColumn[] = powerCriteria.map((c) => {
    const raw = conditionName(c.condition);
    return { rawName: raw, label: shortenRequirementLabel(raw) };
  });
  const personById = new Map(persons.map((p) => [p.id, p]));
  const rows: RequirementRow[] = factSkill.teamMembers
    .map((member) => {
      const cells = columns.map((col) => {
        const item = member.items.find((i) => conditionName(extractCondition(i.criterion)) === col.rawName);
        return { label: col.label, matched: Boolean(item?.matched) };
      });
      const score = cells.filter((c) => c.matched).length;
      return {
        personId: member.personId,
        personName: personById.get(member.personId)?.name ?? member.personId,
        score,
        cells,
      };
    })
    // score = Anzahl erfuellter Element-Kriterien; ersetzt Sortierung nach dem entfernten
    // member.totalPercent (@adr SPARK-0030-match-result-scoring-atom-derived@1 §2).
    .sort((a, b) => b.score - a.score);
  return { columns, rows };
}

// Per-requirement and per-group fulfillment derived client-side. Genesis's
// `factSkill` block carries per-person `items[].matched` (does this person
// satisfy the predicate?) and per-criterion `condition.composition` (the
// constraint: MIN/MAX/EXACT/ALL/ANY + count). We roll matchedCount up to a
// team-level fulfilled flag per requirement here — `teamMatch.conditionResults`
// from genesis only covers a subset of conditions, so we can't rely on it.
// The `factSkill.totalPercent` and `groupResultsPercent` genesis emits are
// person-weighted (matches ÷ persons × criteria) and don't answer "how many
// requirements does the team fulfill" — which is what this section shows.
export interface FactSkillFulfillment {
  totalFulfilled: number;
  totalCount: number;
  totalPercent: number;
  groups: Partial<Record<FactSkillGroupKey, { fulfilled: number; total: number; percent: number }>>;
  // Indexed by the criterion's position within factSkill.criteria. Name-based
  // indexing would collapse columns when genesis omits condition.name (which
  // happens for typed MatchCondition payloads — only a synthesised label).
  byCriterionIndex: boolean[];
  // Muss/Kann je Kriterium (aus dem isMust-Atom bzw. condition.requirement) und die Zahl der
  // vom Team verletzten Muss-Kriterien — damit Assess/Optimize Muss-Verstoesse unabhaengig vom
  // Score sichtbar machen (spark #151, @adr SPARK-0030-match-result-scoring-atom-derived@1 §1).
  mustByCriterionIndex: boolean[];
  violatedMustCount: number;
}

type CompositionOp = "MIN" | "MAX" | "EXACT" | "ALL" | "ANY";

export function readComposition(condition: unknown): { operator: CompositionOp; value: number | null } | null {
  if (!condition || typeof condition !== "object") return null;
  const comp = (condition as { composition?: unknown }).composition;
  if (!comp || typeof comp !== "object") return null;
  const op = (comp as { operator?: unknown }).operator;
  const val = (comp as { value?: unknown }).value;
  if (op !== "MIN" && op !== "MAX" && op !== "EXACT" && op !== "ALL" && op !== "ANY") return null;
  return { operator: op, value: typeof val === "number" ? val : null };
}

function isCriterionFulfilled(condition: unknown, matchedCount: number, teamSize: number): boolean {
  const comp = readComposition(condition);
  // Fallback when composition is missing or unrecognised: treat "anyone matches" as fulfilled.
  if (!comp) return matchedCount >= 1;
  switch (comp.operator) {
    case "MIN":
      return comp.value !== null && matchedCount >= comp.value;
    case "MAX":
      return comp.value !== null && matchedCount <= comp.value;
    case "EXACT":
      return comp.value !== null && matchedCount === comp.value;
    case "ALL":
      return teamSize > 0 && matchedCount === teamSize;
    case "ANY":
      return matchedCount >= 1;
  }
}

export function buildFactSkillFulfillment(
  factSkill: FactSkillView | null,
  _teamMatch: TeamMatchView | null,
): FactSkillFulfillment {
  const empty: FactSkillFulfillment = {
    totalFulfilled: 0,
    totalCount: 0,
    totalPercent: 0,
    groups: {},
    byCriterionIndex: [],
    mustByCriterionIndex: [],
    violatedMustCount: 0,
  };
  if (!factSkill) return empty;

  const teamSize = factSkill.teamMembers.length;
  const byCriterionIndex: boolean[] = [];
  const mustByCriterionIndex: boolean[] = [];
  let violatedMustCount = 0;
  const groupAcc = new Map<string, { fulfilled: number; total: number }>();
  let totalFulfilled = 0;

  for (let idx = 0; idx < factSkill.criteria.length; idx++) {
    const c = factSkill.criteria[idx]!;
    let matched = 0;
    for (const m of factSkill.teamMembers) {
      if (m.items[idx]?.matched) matched += 1;
    }
    const fulfilled = isCriterionFulfilled(c.condition, matched, teamSize);
    byCriterionIndex.push(fulfilled);
    // isMust-Atom des ersten Members bevorzugt (@adr SPARK-0030-match-result-scoring-atom-derived@1), sonst condition.requirement.
    const firstItem = factSkill.teamMembers[0]?.items[idx];
    const must = firstItem
      ? itemRequirement(firstItem) === "MUST"
      : (c.condition as { requirement?: unknown } | null)?.requirement === "MUST";
    mustByCriterionIndex.push(must);
    if (must && !fulfilled) violatedMustCount += 1;
    const acc = groupAcc.get(c.group) ?? { fulfilled: 0, total: 0 };
    acc.total += 1;
    if (fulfilled) {
      acc.fulfilled += 1;
      totalFulfilled += 1;
    }
    groupAcc.set(c.group, acc);
  }

  const groups: FactSkillFulfillment["groups"] = {};
  for (const [g, v] of groupAcc.entries()) {
    groups[g as FactSkillGroupKey] = {
      fulfilled: v.fulfilled,
      total: v.total,
      percent: v.total === 0 ? 0 : v.fulfilled / v.total,
    };
  }

  const totalCount = factSkill.criteria.length;
  return {
    totalFulfilled,
    totalCount,
    totalPercent: totalCount === 0 ? 0 : totalFulfilled / totalCount,
    groups,
    byCriterionIndex,
    mustByCriterionIndex,
    violatedMustCount,
  };
}

// Team-Anforderungsanalyse fuer die "Match-Analyse" (Optimize/Assess): pro Kriterium, wie das
// GANZE Team es erfuellt — Composition-Gate (MIN/MAX/... via isCriterionFulfilled) plus die
// classification-Aufschluesselung je Mitglied (@adr SPARK-0030-match-result-scoring-atom-derived@1 §4). met/close/missing/unrated kommen
// direkt aus der genesis-classification, `proximity` mittelt den Erfuellungsgrad der bewertbaren
// Mitglieder (UNKNOWN ausgenommen) und rankt "fast erreicht" gegen "grosse Luecke". Kein neuer
// Backend-Bedarf: dieselben Atome, die RANK schon nutzt, existieren in jedem Modus.
export interface RequirementAnalysis {
  index: number;
  condition: unknown;
  group: FactSkillGroupKey;
  isMust: boolean;
  fulfilled: boolean;
  operator: CompositionOp;
  required: number | null;
  teamSize: number;
  met: number; // SATISFIED
  close: number; // PARTIAL — hat die Faehigkeit, aber unter dem Soll-Level
  unrated: number; // UNKNOWN — vorhanden, aber nicht bewertbar (spark#175)
  missing: number; // VIOLATED / kein Nachweis
  // Mittlere Erfuellung ueber bewertbare Mitglieder (fulfillment != null), 0..1. Treibt close↔far.
  proximity: number;
  // personIds der SATISFIED-Mitglieder. met === 1 -> genau eine Person traegt die Anforderung
  // (Single Point of Failure): geht sie, faellt die Anforderung. Ermoeglicht "nur {Name}" (spark #236).
  metPersonIds: string[];
  // personIds der PARTIAL-Mitglieder. Die Zaehlung "2 knapp" sagt nicht, WER knapp ist - und
  // genau das ist bei einer Luecke die Frage: niemand in der Naehe oder zwei knapp daneben?
  closePersonIds: string[];
  // Geforderte Fachwissen-Stufe (1..7) aus der Bedingung selbst - NUR bei ExpertiseCondition,
  // sonst null (Department/Sprache/Nationalitaet usw. kennen keine Stufen-Skala). condition.level
  // ist auf genesis-Seite ein Storage-Prozent, keine Stufe direkt (siehe levelFromPercent).
  requiredLevel: number | null;
  // Tatsaechlich erreichte Stufe (1..7), gemittelt ueber alle Mitglieder mit bewertetem Level -
  // NUR bei ExpertiseCondition, sonst null. Antwortet "wie korrekt wurde die geforderte Stufe
  // getroffen", waehrend met/teamSize nur "wie viele haben es ueberhaupt" beantwortet - zwei
  // unabhaengige Luecken bei Fachwissen: mengenmaessig (zu wenige Traeger) und stufenbezogen
  // (Traeger vorhanden, aber nicht auf der geforderten Hoehe).
  actualLevel: number | null;
  // Pro-Person-Details fuer den Diagramm-Popover (RequirementMatrixDiagram): eigene Stufe und
  // KI-Begruendungstext dieser Person zu GENAU diesem Kriterium - by personId, nur fuer Mitglieder
  // mit einem Item (fehlende Items zaehlen oben schon als "missing"). level ist wie actualLevel
  // null, wenn das Kriterium keine Stufen-Skala hat oder die Person unbewertet ist.
  personDetails: Record<string, { level: SkillLevel | null; aiReason: string | null }>;
}

// ponytail: 0.5 = "Team ist im Schnitt mindestens halb am Kann-Ziel" -> Bucket "fast erreicht".
// Einziger Regler hier; anpassen, falls die close/far-Grenze sich falsch anfuehlt.
export const REQUIREMENT_CLOSE_THRESHOLD = 0.5;

// condition.level ist ein Storage-Prozent (siehe levelFromPercent), keine Stufe 1..7 direkt -
// und nur ExpertiseCondition hat ueberhaupt ein Level. Andere Konditionstypen (IndustryExperience
// z.B.) haben ein anderes "value"-Feld, das faelschlich wie ein Level aussehen wuerde, wenn man
// es blind durch levelFromPercent jagt - deshalb der Typename-Guard.
function requiredSkillLevel(condition: unknown): number | null {
  if (conditionTypename(condition) !== "ExpertiseCondition") return null;
  const level = (condition as { level?: unknown } | null)?.level;
  return typeof level === "number" ? (levelFromPercent(level)?.value ?? null) : null;
}

export function buildRequirementAnalysis(factSkill: FactSkillView | null): RequirementAnalysis[] {
  if (!factSkill) return [];
  const teamSize = factSkill.teamMembers.length;
  return factSkill.criteria.map((c, idx) => {
    let met = 0;
    let close = 0;
    let unrated = 0;
    let missing = 0;
    let matched = 0;
    let fulfillmentSum = 0;
    let fulfillmentCount = 0;
    const metPersonIds: string[] = [];
    const closePersonIds: string[] = [];
    const requiredLevel = requiredSkillLevel(c.condition);
    let actualLevelSum = 0;
    let actualLevelCount = 0;
    const personDetails: Record<string, { level: SkillLevel | null; aiReason: string | null }> = {};
    for (const m of factSkill.teamMembers) {
      const it = m.items[idx];
      if (!it) {
        missing += 1;
        continue;
      }
      if (it.matched) matched += 1;
      // classification ist die massgebliche Zuordnung (@adr SPARK-0030-match-result-scoring-atom-derived@1 §4); Fallback auf matched fuer Alt-Blobs.
      const cls = it.classification ?? (it.matched ? "SATISFIED" : "VIOLATED");
      if (cls === "SATISFIED") {
        met += 1;
        metPersonIds.push(m.personId);
      }
      else if (cls === "PARTIAL") {
        close += 1;
        closePersonIds.push(m.personId);
      }
      else if (cls === "UNKNOWN") unrated += 1;
      else missing += 1;
      // UNKNOWN traegt fulfillment=null und bleibt aus dem Mittel; sonst wuerde "unbekannt" wie 0 wirken.
      if (it.fulfillment != null) {
        fulfillmentSum += it.fulfillment;
        fulfillmentCount += 1;
      }
      // item.actual ist bei ExpertiseCondition ebenfalls Storage-Prozent (String) - erst
      // levelFromPercent bringt es auf dieselbe 1..7-Skala wie requiredLevel. Unbewertete
      // Werte (0/leer) liefern dort bereits null, bleiben also aus dem Mittel raus.
      let personLevel: SkillLevel | null = null;
      if (requiredLevel != null && it.actual != null) {
        const level = levelFromPercent(Number(it.actual));
        if (level != null) {
          actualLevelSum += level.value;
          actualLevelCount += 1;
          personLevel = level;
        }
      }
      personDetails[m.personId] = { level: personLevel, aiReason: it.aiReason ?? null };
    }
    const comp = readComposition(c.condition);
    const firstItem = factSkill.teamMembers[0]?.items[idx];
    const isMust = firstItem
      ? itemRequirement(firstItem) === "MUST"
      : (c.condition as { requirement?: unknown } | null)?.requirement === "MUST";
    return {
      index: idx,
      condition: c.condition,
      group: c.group as FactSkillGroupKey,
      isMust,
      fulfilled: isCriterionFulfilled(c.condition, matched, teamSize),
      operator: comp?.operator ?? "ANY",
      required: comp?.value ?? null,
      teamSize,
      met,
      close,
      unrated,
      missing,
      proximity: fulfillmentCount === 0 ? 0 : fulfillmentSum / fulfillmentCount,
      metPersonIds,
      closePersonIds,
      requiredLevel,
      actualLevel: actualLevelCount === 0 ? null : actualLevelSum / actualLevelCount,
      personDetails,
    };
  });
}

// Zusammensetzungs-Auflage der Zeile, als {id, values} statt fertigem Text - so ist die
// Sichtbarkeitsregel ohne Rendering testbar. null = nichts anzeigen:
// - MIN 1 / ANY ist die Voreinstellung ("irgendwer") und sagt nichts,
// - MAX/EXACT ohne Schwelle waere eine Auflage ohne Zahl.
// Wichtig: NICHT nur MIN behandeln, sonst verliert genau die MAX-Zeile ihre Auflage.
// Liegt hier statt in ResultPanel, weil der PDF-Renderer ein eigenes Bundle ohne DOM-Komponenten
// ist - ein Import aus ResultPanel zoege 4000 Zeilen React-UI in den Renderer-Entry.
export function compositionNote(
  a: Pick<RequirementAnalysis, "operator" | "required">,
): { id: string; values: Record<string, number> } | null {
  const n = a.required;
  switch (a.operator) {
    case "ALL":
      return { id: "match.result.analysis.rowCompositionAll", values: {} };
    case "MIN":
      return n != null && n >= 2 ? { id: "match.result.analysis.rowCompositionMin", values: { n } } : null;
    case "MAX":
      return n != null ? { id: "match.result.analysis.rowCompositionMax", values: { n } } : null;
    case "EXACT":
      return n != null ? { id: "match.result.analysis.rowCompositionExact", values: { n } } : null;
    default:
      return null;
  }
}

// Radar-Nullpunkt: die Cluster-Scores sind Mittel aus bis zu 30 Selbsteinschaetzungen und landen
// praktisch immer im Band 60-75 - bei Nullpunkt 0 zeichnen zwei voellig verschiedene Teams
// dasselbe regulaere Sechseck.
export const RADAR_ORIGIN = 50;

// Wunsch (0-10) als Zielniveau auf der Radialskala: 0 = "egal" (= Nullpunkt), 10 = das obere
// Ende. Beim Cluster-Radar ist das Ende 100, also target = 50 + 5*wunsch.
export function wishTarget(wish: number, max: number): number {
  return RADAR_ORIGIN + (max - RADAR_ORIGIN) * (Math.min(10, wish) / 10);
}

// ─── Absolute Skill-Struktur: das ganze Kompetenz-Inventar des Teams (@external-adr GENESIS-0032-match-result-blob-carries-expertise-atoms) ───
// Anders als buildRequirementAnalysis (nur GEFRAGTE Skills) sieht das ALLE Fachkompetenzen, die
// die Mitglieder mitbringen. "Stark" ab SKILL_STRONG_LEVEL; strong === 1 ist ein Single Point of
// Failure (die Kompetenz ruht auf einer Person). Leer, wenn der Blob kein Inventar trägt (RANK,
// Alt-Blobs, Matches ohne geladenes Fachwissen) -> Panel blendet sich aus.
// ponytail: 4 = "Practitioner" von 7 — wendet die Kompetenz praktisch an. Einziger Regler; anpassen,
// falls "stark" sich zu streng/lasch anfühlt.
export const SKILL_STRONG_LEVEL = 4;

export interface SkillCoverage {
  skill: string;
  strong: number; // Mitglieder mit der Kompetenz auf Stufe >= SKILL_STRONG_LEVEL
  total: number; // Mitglieder, die die Kompetenz überhaupt haben (Breite)
  // Höchste Stufe im Team (0 = niemand eingestuft). Tiefe neben der Breite: "1 stark, 1 überhaupt"
  // und "1 stark, 4 überhaupt" sind verschiedene Risiken und lesen sich ohne total/maxLevel gleich.
  maxLevel: number;
  // Stand die Kompetenz als ExpertiseCondition in den Anforderungen? false = das Team bringt sie
  // mit, ohne dass jemand danach gefragt hat.
  requested: boolean;
  strongPersonIds: string[]; // wer sie stark hat — bei strong === 1 der SPOF-Name
  // Beste Stufe je Person für diese Kompetenz — treibt die Skill-Matrix (Kompetenzen × Mitglieder).
  levelByPersonId: Record<string, number>;
}

export function buildSkillCoverage(
  factSkill: FactSkillView | null,
  threshold: number = SKILL_STRONG_LEVEL,
): SkillCoverage[] {
  if (!factSkill) return [];
  // Gefragte Fachkompetenzen aus denselben Kriterien, die buildRequirementAnalysis auswertet — so
  // weiss das Inventar, was verlangt war; der Rest ist "bringt das Team ausserdem mit" (spark #236).
  const requestedSkills = new Set(
    factSkill.criteria
      .filter((c) => conditionTypename(c.condition) === "ExpertiseCondition")
      .map((c) => String((c.condition as { expertise?: unknown }).expertise ?? "").trim().toLowerCase())
      .filter(Boolean),
  );
  const byName = new Map<
    string,
    { display: string; strong: Set<string>; total: Set<string>; levels: Map<string, number> }
  >();
  for (const m of factSkill.teamMembers) {
    for (const e of m.expertises ?? []) {
      const name = (e.description ?? "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      const acc =
        byName.get(key) ?? { display: name, strong: new Set<string>(), total: new Set<string>(), levels: new Map<string, number>() };
      acc.total.add(m.personId);
      const level = e.level ?? 0;
      // Beste Stufe je Person behalten (falls eine Kompetenz doppelt gelistet ist).
      acc.levels.set(m.personId, Math.max(acc.levels.get(m.personId) ?? 0, level));
      if (e.level != null && e.level >= threshold) acc.strong.add(m.personId);
      byName.set(key, acc);
    }
  }
  return Array.from(byName.entries())
    .map(([key, v]) => ({
      skill: v.display,
      strong: v.strong.size,
      total: v.total.size,
      maxLevel: Math.max(0, ...v.levels.values()),
      requested: requestedSkills.has(key),
      strongPersonIds: [...v.strong],
      levelByPersonId: Object.fromEntries(v.levels),
    }))
    .sort((a, b) => b.strong - a.strong || b.total - a.total || a.skill.localeCompare(b.skill));
}

// Best-effort human label for a FactSkill criterion. Genesis sometimes emits
// a synthesised `name` field, sometimes only the typed MatchCondition shape.
// `labels` is an id → name map for the ID-based conditions (department,
// location, country, gender, marital) — pass an empty map if unavailable.
export interface ConditionLabelLookup {
  jobDepartments: Map<string, string>;
  jobLocations: Map<string, string>;
  countries: Map<string, string>;
  genders: Map<string, string>;
  maritalStatuses: Map<string, string>;
  industries: Map<string, string>;
}

export interface ConditionLabelFormatters {
  labels: {
    department: string;
    location: string;
    country: string;
    citizenship: string;
    gender: string;
    maritalStatus: string;
    age: string;
    annualSalary: string;
    certificate: string;
    children: string;
    city: string;
    driversLicense: string;
    education: string;
    experience: string;
    industry: string;
    nationality: string;
    expertise: string;
    language: string;
  };
  exact(label: string, value: string, operator: unknown): string;
  present(label: string): string;
  threshold(label: string, value: string, operator: unknown): string;
  years(years: number): string;
  languageName(tag: string): string;
  // Skill-Stufe als benannte Stufe ("Contributor") statt roher Zahl ("3") — die Zahl kollidiert
  // sonst mit Personen-Zählungen im Ergebnis (spark #236). Namen sind die Smycles-Stufenskala 1–7.
  skillLevelName(level: SkillLevel): string;
}

export const EMPTY_LABEL_LOOKUP: ConditionLabelLookup = {
  jobDepartments: new Map(),
  jobLocations: new Map(),
  countries: new Map(),
  genders: new Map(),
  maritalStatuses: new Map(),
  industries: new Map(),
};

/** Sentinel returned by {@link labelForCondition} when a condition can't be labelled. */
export const CONDITION_LABEL_UNKNOWN = "–";

export function labelForCondition(
  condition: unknown,
  lookup: ConditionLabelLookup,
  formatters: ConditionLabelFormatters,
): string {
  if (!condition || typeof condition !== "object") return CONDITION_LABEL_UNKNOWN;
  const obj = condition as Record<string, unknown>;
  const typename = typeof obj.__typename === "string" ? obj.__typename : "";
  const { labels } = formatters;

  switch (typename) {
    case "JobDepartmentCondition":
      return formatters.exact(labels.department, resolveId(obj.id, lookup.jobDepartments), obj.operator);
    case "JobLocationCondition":
      return formatters.exact(labels.location, resolveId(obj.id, lookup.jobLocations), obj.operator);
    case "CountryCondition":
      return formatters.exact(labels.country, resolveId(obj.id, lookup.countries), obj.operator);
    case "CitizenshipCondition":
      return formatters.exact(labels.citizenship, resolveId(obj.id, lookup.countries), obj.operator);
    case "GenderCondition":
      return formatters.exact(labels.gender, resolveId(obj.id, lookup.genders), obj.operator);
    case "MaritalCondition":
      return formatters.exact(labels.maritalStatus, resolveId(obj.id, lookup.maritalStatuses), obj.operator);
    case "AgeCondition":
      return formatters.threshold(labels.age, stringOf(obj.value), obj.operator);
    case "AnnualSalaryCondition":
      return formatters.threshold(labels.annualSalary, stringOf(obj.value), obj.operator);
    case "CertificateCondition":
      return formatters.exact(labels.certificate, stringOf(obj.value), obj.operator);
    case "ChildrenCountCondition":
      return formatters.threshold(labels.children, stringOf(obj.value), obj.operator);
    case "CityCondition": {
      const city = stringOf(obj.value);
      const radius = typeof obj.radiusKm === "number" ? obj.radiusKm : null;
      return formatters.exact(labels.city, radius == null ? city : `${city} · ${radius} km`, obj.operator);
    }
    case "DriversLicenseCondition":
      return formatters.exact(labels.driversLicense, stringOf(obj.value), obj.operator);
    case "EducationCondition":
      return formatters.exact(labels.education, stringOf(obj.value), obj.operator);
    case "ExperienceCondition":
      // Freitext-Erfahrung (nur EQ/NE, siehe FreitextSection) ist kategorial, kein ordinaler Wert:
      // niemals "mind./max." — das gilt nur für Stufen (Skills) und Mengen (Jahre/Alter). exact liefert
      // "Berufserfahrung: {value}" bzw. bei NE "… nicht {value}".
      return formatters.exact(labels.experience, stringOf(obj.value), obj.operator);
    case "IndustryExperienceCondition": {
      const years = typeof obj.minimumYears === "number" ? obj.minimumYears : null;
      const industryId = typeof obj.industryId === "string" ? obj.industryId : "";
      const industry = industryId ? resolveId(industryId, lookup.industries) : stringOf(obj.name) || labels.industry;
      return years != null ? formatters.threshold(industry, formatters.years(years), obj.operator) : industry;
    }
    case "NationalityCondition":
      return formatters.exact(labels.nationality, stringOf(obj.value), obj.operator);
    case "ExpertiseCondition": {
      const skillName = typeof obj.expertise === "string" && obj.expertise.trim()
        ? obj.expertise
        : stringOf(obj.name) || labels.expertise;
      if (typeof obj.level === "number" && obj.level <= 0) return formatters.present(skillName);
      const skillLevel = typeof obj.level === "number" ? levelFromPercent(obj.level) : null;
      const level = skillLevel
        ? formatters.skillLevelName(skillLevel)
        : typeof obj.level === "number"
          ? String(obj.level)
          : "";
      return level ? formatters.threshold(skillName, level, obj.operator) : skillName;
    }
    case "LanguageCondition": {
      const tag = typeof obj.languageTag === "string" ? obj.languageTag : "";
      const name = tag ? formatters.languageName(tag) : labels.language;
      const level = typeof obj.level === "string" ? obj.level : "";
      return level ? formatters.threshold(name, level, obj.operator) : name;
    }
    default:
      if (typeof obj.name === "string" && obj.name.length > 0) return obj.name;
      return typename.replace(/Condition$/, "") || CONDITION_LABEL_UNKNOWN;
  }
}

// Soll-Wert eines Kriteriums auf derselben Skala wie die actual-Verteilung
// (Expertise: Storage-Prozent 0..100, nicht Stufe 1..7), fuer den Soll-Marker
// auf der Verteilungs-Achse. null = Kriterium hat keinen numerischen Soll-Wert.
export function conditionNumericThreshold(condition: unknown): number | null {
  if (!condition || typeof condition !== "object") return null;
  const obj = condition as Record<string, unknown> & { __typename?: string };
  switch (obj.__typename) {
    case "ExpertiseCondition":
      return typeof obj.level === "number" && obj.level > 0 ? obj.level : null;
    case "IndustryExperienceCondition":
      return typeof obj.minimumYears === "number" ? obj.minimumYears : null;
    default:
      return typeof obj.value === "number" ? obj.value : null;
  }
}

function resolveId(id: unknown, map: Map<string, string>): string {
  if (typeof id !== "string") return "?";
  return map.get(id) ?? id;
}

function stringOf(v: unknown): string {
  return v === undefined || v === null ? "" : String(v);
}

// "min 1", "max 5", "exact 2", "all", "any" — mirrors ai-research's
// formatConstraint for a familiar inline summary next to each requirement.
export function constraintSummary(condition: unknown): string {
  if (!condition || typeof condition !== "object") return "";
  const comp = (condition as { composition?: unknown }).composition;
  if (!comp || typeof comp !== "object") return "";
  const op = (comp as { operator?: unknown }).operator;
  const val = (comp as { value?: unknown }).value;
  switch (op) {
    case "MIN":
      return typeof val === "number" ? `min ${val}` : "min";
    case "MAX":
      return typeof val === "number" ? `max ${val}` : "max";
    case "EXACT":
      return typeof val === "number" ? `exact ${val}` : "exact";
    case "ALL":
      return "all";
    case "ANY":
      return "any";
    default:
      return "";
  }
}

// --- Bewerbung-Ranking (Muss-Gate + Gesamt-Erfüllung, Cluster A/B/C/unqualifiziert) ---
// "compute once, threshold many": genesis liefert je Kriterium requirement/fulfillment,
// die Clusterung passiert hier im Frontend und der Slider verschiebt nur die Schwellen.
// REVIEW = "zu pruefen" (spark#175): der Kandidat ist nicht widerlegt, aber auch nicht
// rankbar, weil ein Muss unbewertbar ist oder kein einziges Kann bewertet werden konnte.
export type RankCluster = "A" | "B" | "C" | "REVIEW" | "UNQUALIFIED";

// Muss-Gate mit drei Ausgaengen (spark#175 / @adr SPARK-0030-match-result-scoring-atom-derived@1 §1). UNQUALIFIED entsteht nur aus
// nachgewiesener Verletzung, nie aus fehlender Information.
export type RankQualification = "QUALIFIED" | "REVIEW_REQUIRED" | "UNQUALIFIED";

export interface RankThresholds {
  a: number;
  b: number;
  // Untergrenze fuer C: darunter faellt ein Bewerber (trotz erfuellter Muss) aus A/B/C
  // in die unterste Gruppe. Optional, Default 0 = kein Score-Boden.
  c?: number;
}

export const DEFAULT_RANK_THRESHOLDS: RankThresholds = { a: 0.8, b: 0.5, c: 0 };

export interface RankedCriterion {
  condition: unknown;
  requirement: string;
  // null = nicht bewertbar (classification UNKNOWN). Nicht 0: "unbekannt" ist keine Null-Erfuellung.
  fulfillment: number | null;
  actual: string | null;
  aiReason: string | null;
  assessable: boolean;
}

export type RequirementLeniencyView = "LENIENT" | "MUST_STRICT" | "STRICT";

// Kulanz-Treffer: das Kriterium gilt als erfuellt, obwohl die geforderte Mindeststufe nicht
// belegt ist. Ohne genesis-Feld ableitbar: bei kulanter Anforderungspruefung stellt der
// AI-Fallback die Stufen-Frage gar nicht, und aiReason ist nur gesetzt, wenn der (immer
// stufen-strikte) Hard-Check vorher scheiterte. Jedes per AI bestandene Fachwissen-Kriterium
// mit gefordertem Level ist unter Kulanz also "vorhanden, Stufe nicht verifiziert".
// leniency kommt aus dem Design-Snapshot des Results (null = LENIENT, genesis-Default).
export function isLenientLevelMatch(
  condition: unknown,
  requirement: string,
  aiReason: string | null | undefined,
  met: boolean,
  leniency: RequirementLeniencyView | null | undefined,
): boolean {
  if (!met || !aiReason?.trim()) return false;
  const effective = leniency ?? "LENIENT";
  if (effective === "STRICT") return false;
  if (effective === "MUST_STRICT" && requirement === "MUST") return false;
  const c = condition as { __typename?: string; level?: number } | null;
  return c?.__typename === "ExpertiseCondition" && typeof c?.level === "number" && c.level > 0;
}

// ─── Kulanz-Abhaengigkeit (spark #236) ──────────────────────────────────────
// Kulanz ist heute nur im Ranken-Modus sichtbar; in Optimize/Assess lesen "erfuellt" und
// "kulant erfuellt" identisch. Das ist die Bruchstelle: ein Kriterium, das nur haelt, weil
// die geforderte Stufe nicht belegt sein muss, ist keine harte Erfuellung.
// Kriterien, die heute team-weit erfuellt sind und es ohne die Kulanz-Treffer nicht waeren.
// Die Kulanz-Treffer selbst zaehlt niemand: sie stehen an ihrer Zelle (Marke "Kulant"), erst die
// Abhaengigkeit ist eine Nachricht ueber das Team.
export function leniencyDependentCriteria(
  factSkill: FactSkillView | null,
  leniency: RequirementLeniencyView | null | undefined,
): number[] {
  if (!factSkill) return [];
  const teamSize = factSkill.teamMembers.length;
  const criteriaIndexes: number[] = [];
  factSkill.criteria.forEach((c, idx) => {
    let matched = 0;
    let lenient = 0;
    for (const m of factSkill.teamMembers) {
      const item = m.items[idx];
      if (!item) continue;
      if (item.matched) matched += 1;
      // Dieselbe Regel wie im Ranken-Modus, damit ein Treffer nicht je Modus anders heisst.
      const condition = c.condition ?? extractCondition(item.criterion);
      const met = isAssessable(item) && itemFulfillment(item) >= 1;
      if (isLenientLevelMatch(condition, itemRequirement(item), item.aiReason, met, leniency)) lenient += 1;
    }
    if (lenient === 0) return;
    // Dasselbe Composition-Gate, nur ohne die Kulanz-Treffer: kippt es, haengt das Kriterium daran.
    // MAX-Auflagen koennen dadurch auch erfuellt BLEIBEN - deshalb neu rechnen statt zu zaehlen.
    // Math.max: ein Kulanz-Treffer ohne matched-Flag (fulfillment=1, matched=false) wuerde sonst
    // unter 0 zaehlen und eine MAX-Auflage scheinbar retten.
    if (
      isCriterionFulfilled(c.condition, matched, teamSize) &&
      !isCriterionFulfilled(c.condition, Math.max(0, matched - lenient), teamSize)
    ) {
      criteriaIndexes.push(idx);
    }
  });
  return criteriaIndexes;
}

export interface RankedCandidate {
  personId: string;
  personName: string;
  cluster: RankCluster;
  // null = nicht bewertbar: es gibt Kann-Kriterien, aber keines davon liess sich bewerten.
  // Ausdruecklich nicht 0 (das hiesse "erfuellt nichts") und nicht 1 (das hiesse "erfuellt alles").
  score: number | null;
  qualification: RankQualification;
  /** Backward-compatible boolean view of the MUST qualification. */
  qualified: boolean;
  metCount: number;
  totalCount: number;
  // Muss-Erfuellung getrennt ausweisbar (spark #151): der KANN-only-Score sagt nichts ueber die
  // bestandenen Muss-Kriterien, ein qualifizierter Kandidat mit schwachem KANN darf nicht als
  // "0 %"-Versager gelesen werden. mustMet/mustTotal treibt den Muss-Chip in der Zeile.
  mustMet: number;
  mustTotal: number;
  // KANN-Erfuellung fuer den Kann-Chip: Summe der Erfuellungsgrade, NICHT die Anzahl voll
  // erfuellter Kriterien. genesis liefert graduelle Werte (ratioFulfillment fuer minimumYears),
  // deshalb waere ein Zaehler irrefuehrend: 3x 0.8 erfuellt kein einziges Kriterium voll, ergibt
  // aber Score 80 %. kannSum/kannTotal === score; auf der Anforderungs-Achse kann das Badge
  // damit nicht vom Band abweichen (auf den anderen Achsen clustert deren Score, s. axisScore).
  kannSum: number;
  // Nur die bewertbaren Kann-Kriterien, damit kannSum/kannTotal === score erhalten bleibt.
  kannTotal: number;
  // Abdeckung (spark#175): wie viele Kann-Kriterien ueberhaupt definiert sind. kannTotal von
  // kannDefined ist die Belastbarkeit des Scores und wird getrennt angezeigt, nie einmultipliziert.
  kannDefined: number;
  criteria: RankedCriterion[];
  // Zweite, ungemischte Achse (@adr SPARK-0030-match-result-scoring-atom-derived@1 / spark #151): gewichtetes Mittel der eigenen
  // Element-Scores des Kandidaten. null, wenn keine Powers vorliegen.
  powerFit: number | null;
  // Dritte, unabhaengige Achse: ganzheitliche AI-Eignung (0-1) aus aiAssessment.suitability
  // (genesis-Bewertung 0-100). null, wenn die AI keine Eignung geliefert hat (z.B. Alt-Blobs).
  suitabilityScore: number | null;
  teamFit?: number | null;
}

// Sortier-Achse der Rank-Liste. "requirements" = Anforderungs-Fit (Default, wie bisher),
// "power" = Power-Fit. Muss-Gate bleibt in beiden Faellen hartes Kriterium (qualifiziert zuerst).
export type RankSortBy = "smycles" | "requirements" | "power" | "team";

// Power-Fit je Kandidat: gewichtetes Mittel der eigenen Element-Scores (0-100), Gewicht aus den
// Team-Power-Wuenschen (0-1000, elementWeights je CreativityElement). Fehlt ein Gewicht,
// entspricht 100 dem frueheren V1-Fallback 1. Unabhaengig vom Anforderungs-Fit.
//
// Wer keinen einzigen bewerteten Wert hat, steht GAR NICHT in der Map - vorher stand dort eine 0.
// Alle Leser holen den Wert ueber `.get(...) ?? null`, tragen die Abwesenheit also schon; die
// Schnitte darueber (buildTeamPowerFit, buildWishFit) mittelten dagegen ueber die Nullen und
// druckten das Ergebnis unter dem Wort "Schnitt" - dieselbe Luecke wie in memberPowerScore.
export function buildPowerFit(
  powers: PowersView | null,
  elementWeights?: Readonly<Record<string, number>>,
): Map<string, number> {
  const byPerson = new Map<string, number>();
  if (!powers) return byPerson;
  for (const member of powers.teamMembers) {
    let weightSum = 0;
    let weightedValueSum = 0;
    for (const item of member.items) {
      if (item.value == null) continue;
      const weight = elementWeights?.[item.criterion.element] ?? 100;
      if (weight <= 0) continue;
      weightSum += weight;
      weightedValueSum += weight * item.value;
    }
    if (weightSum > 0) byPerson.set(member.personId, weightedValueSum / weightSum);
  }
  return byPerson;
}

// Team-Power-Fit: gewichtetes Mittel aller Element-Werte des Teams nach den
// Team-Power-Wuenschen. Gleiche Formel wie buildPowerFit, nur ueber alle Mitglieder
// gemittelt. null, wenn es keine Wuensche oder keine bewerteten Werte gibt - dann gibt
// es keine Fit-Aussage, nur das Ist-Profil.
export function buildTeamPowerFit(
  powers: PowersView | null,
  elementWeights?: Readonly<Record<string, number>>,
): number | null {
  if (!powers || !elementWeights) return null;
  const perPerson = Array.from(buildPowerFit(powers, elementWeights).values());
  if (perPerson.length === 0) return null;
  return perPerson.reduce((sum, v) => sum + v, 0) / perPerson.length;
}

// ─── Ist gegen Wunsch: eine Zahl, die nur als Differenz etwas sagt (spark #236) ───
// Beide Seiten kommen aus buildPowerFit, also aus derselben Formel und ueber dieselben Items.
// Vorher stand die Ist-Seite als genesis-totalPercent neben einer im Frontend gerechneten
// Fit-Zahl: eine Differenz zweier Definitionen ist ein Phantom-Delta.
// UNIFORM ist kein Befund, sondern Datenlage: sind alle Gewichte gleich, IST das gewichtete
// Mittel das ungewichtete - dann sagt die Differenz nichts, weil es keine Betonung gibt.
export type WishFitVerdict = "NO_WISHES" | "UNIFORM" | "NO_EFFECT" | "ABOVE" | "BELOW";

export interface WishFit {
  // Team-Mittel 0-100, ungewichtet. null, wenn niemand eine Power eingeschaetzt hat.
  actual: number | null;
  // Dasselbe Mittel, mit den Wuenschen gewichtet (Wunsch 0 faellt heraus). null ohne Wuensche.
  weighted: number | null;
  // Nur bei echter Betonung gesetzt - sonst gibt es keine Differenz zu lesen.
  delta: number | null;
  verdict: WishFitVerdict;
}

// ponytail: unter einem Punkt ist die Differenz kein Befund, sondern Rundung. Einziger Regler.
export const WISH_FIT_NOISE = 1;

export function buildWishFit(
  powers: PowersView | null,
  elementWeights?: Readonly<Record<string, number>>,
): WishFit {
  const unweighted = Array.from(buildPowerFit(powers, undefined).values());
  const actual = unweighted.length > 0 ? unweighted.reduce((a, b) => a + b, 0) / unweighted.length : null;
  if (actual == null) return { actual: null, weighted: null, delta: null, verdict: "NO_WISHES" };
  if (!elementWeights) return { actual, weighted: null, delta: null, verdict: "NO_WISHES" };
  const weighted = buildTeamPowerFit(powers, elementWeights);
  if (weighted == null) return { actual, weighted: null, delta: null, verdict: "NO_WISHES" };
  // Wirksame Gewichte inklusive Voreinstellung: ein Gewicht neben 29 Voreinstellungen IST eine
  // Betonung, 30 gleiche Gewichte sind keine.
  const effectiveWeights = (powers?.criteria ?? []).map((c) => elementWeights[c.element] ?? 100);
  // Alles auf "egal" gestellt: buildPowerFit hat dann kein Gewicht zu verteilen und liefert 0 -
  // das waere als "0 %" eine Falschaussage ueber das Team.
  if (!effectiveWeights.some((w) => w > 0)) {
    return { actual, weighted: null, delta: null, verdict: "NO_WISHES" };
  }
  const effective = new Set(effectiveWeights);
  if (effective.size <= 1) return { actual, weighted, delta: null, verdict: "UNIFORM" };
  const delta = weighted - actual;
  const verdict = Math.abs(delta) < WISH_FIT_NOISE ? "NO_EFFECT" : delta > 0 ? "ABOVE" : "BELOW";
  return { actual, weighted, delta, verdict };
}

// Wire-Gewicht -> Wunsch. genesis speichert team_profile_element.weight als 0..1000
// (match-design-rules), die Oberflaeche rechnet auf 0..10 um. OHNE diese Umrechnung liegt
// jedes gesetzte Gewicht ausserhalb der Score-Skala 0..100, und der Ziel-Ring des Radars
// (50 + 5*wunsch) klemmt bei JEDEM Team am Aussenrand - der Wunsch war damit unlesbar.
export function elementWish(rawWeight: number): number {
  return rawWeight / POWERS_WEIGHT_DISPLAY_DIVISOR;
}

// Wunsch-Gewicht je Cluster (Mittel der Element-Wuensche, 0-10). Beschriftet die
// gewichteten Cluster in der Powers-Matrix; leer, wenn kein Profil gesetzt ist.
export function buildClusterWishes(
  powers: PowersView | null,
  elementWeights?: Readonly<Record<string, number>>,
): Map<string, number> {
  const byGroup = new Map<string, number>();
  if (!powers || !elementWeights) return byGroup;
  const acc = new Map<string, { sum: number; count: number }>();
  for (const c of powers.criteria) {
    const w = elementWeights[c.element];
    if (typeof w !== "number") continue;
    const a = acc.get(c.group) ?? { sum: 0, count: 0 };
    a.sum += w;
    a.count += 1;
    acc.set(c.group, a);
  }
  for (const [group, a] of acc) byGroup.set(group, elementWish(a.sum / a.count));
  return byGroup;
}

function itemRequirement(item: FactSkillItemView): string {
  // Breakdown-Atom zuerst (@adr SPARK-0030-match-result-scoring-atom-derived@1 §2): genesis liefert isMust direkt. Fallback auf
  // condition.requirement fuer Alt-Blobs vor #160.
  if (item.isMust != null) return item.isMust ? "MUST" : "OPTIONAL";
  const cond = extractCondition(item.criterion) as { requirement?: unknown } | null;
  return cond?.requirement === "MUST" ? "MUST" : "OPTIONAL";
}

function itemFulfillment(item: FactSkillItemView): number {
  if (typeof item.fulfillment === "number") return item.fulfillment;
  return item.matched ? 1 : 0;
}

// Nicht bewertbar (spark#175): genesis konnte das Kriterium weder bestaetigen noch widerlegen,
// z.B. Fachwissen ohne eingestufte Stufe. Genesis serialisiert das als matched=false +
// fulfillment=null, damit Alt-Consumer nicht brechen; erst classification macht es erkennbar.
// Alt-Blobs ohne classification verhalten sich deshalb unveraendert.
function isAssessable(item: FactSkillItemView): boolean {
  return item.classification !== "UNKNOWN";
}

function qualificationRank(qualification: RankQualification): number {
  if (qualification === "QUALIFIED") return 0;
  return qualification === "REVIEW_REQUIRED" ? 1 : 2;
}

// Kandidaten-Score aus den Breakdown-Atomen: ungewichtetes Mittel der Erfuellung.
// Ersetzt das frueher von genesis gelieferte member.totalPercent (mit #160 entfernt, @adr SPARK-0030-match-result-scoring-atom-derived@1 §2).
// UNKNOWN-Items werden uebersprungen statt als 0 gewertet (spark#175); sind alle Items
// unbewertbar, ist 0 nur ein Sortier-Fallback (einziger Consumer sortiert die Facts-Tabelle).
export function memberFactSkillScore(member: FactSkillMemberView): number {
  const assessed = member.items.filter(isAssessable);
  if (assessed.length === 0) return 0;
  return assessed.reduce((sum, i) => sum + itemFulfillment(i), 0) / assessed.length;
}

// null, wenn diese Person gar keine Einschaetzung hat. Vorher 0 - damit sortierte eine
// unbewertete Person ans Ende der Rangliste und las sich dort wie die schwaechste im Team.
function memberPowerScore(member: PowerMemberView): number | null {
  const values = member.items.map((i) => i.value).filter((v): v is number => v != null);
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

export function buildCandidateRanking(
  factSkill: FactSkillView | null,
  persons: ResultPerson[],
  thresholds: RankThresholds = DEFAULT_RANK_THRESHOLDS,
  powerFitById?: Map<string, number>,
  sortBy: RankSortBy = "smycles",
  teamFitById?: ReadonlyMap<string, number | null>,
): RankedCandidate[] {
  if (!factSkill) return [];
  const personById = new Map(persons.map((p) => [p.id, p]));
  return factSkill.teamMembers
    .map((member): RankedCandidate => {
      let mustFailed = false;
      let mustUnknown = false;
      let kannSum = 0;
      let kannCount = 0;
      let kannDefined = 0;
      let metCount = 0;
      let mustMet = 0;
      let mustTotal = 0;
      const criteria: RankedCriterion[] = [];
      for (const item of member.items) {
        const requirement = itemRequirement(item);
        const assessable = isAssessable(item);
        const fulfillment = assessable ? itemFulfillment(item) : null;
        criteria.push({
          condition: extractCondition(item.criterion),
          requirement,
          fulfillment,
          actual: item.actual ?? null,
          aiReason: item.aiReason ?? null,
          assessable,
        });
        if (fulfillment != null && fulfillment >= 1) metCount += 1;
        if (requirement === "MUST") {
          mustTotal += 1;
          // Fehlende Information ist kein Scheitern: sie schickt den Kandidaten in die
          // Pruefung, statt ihn auszusortieren (spark#175). Nur ein bekannter Wert
          // unterhalb der Anforderung ist eine nachgewiesene Verletzung.
          if (fulfillment == null) mustUnknown = true;
          else if (fulfillment < 1) mustFailed = true;
          else mustMet += 1;
        } else {
          kannDefined += 1;
          if (fulfillment == null) continue;
          kannSum += fulfillment;
          kannCount += 1;
        }
      }
      criteria.sort((a, b) => Number(b.requirement === "MUST") - Number(a.requirement === "MUST"));
      const qualification: RankQualification = mustFailed
        ? "UNQUALIFIED"
        : mustUnknown
          ? "REVIEW_REQUIRED"
          : "QUALIFIED";
      // Score = ungewichtetes Mittel der KANN-Erfuellung (@adr SPARK-0030-match-result-scoring-atom-derived@1 §3 / map #137). MUSS ist reines
      // Gate: bei allen Qualifizierten ohnehin 1.0, es wuerde den Wert nur konstant nach oben
      // verzerren (Kompression + nicht match-uebergreifend vergleichbar). Ohne KANN-Kriterium gibt
      // es nichts zu differenzieren -> 1.0. Sind Kann-Kriterien definiert, aber keines davon
      // bewertbar, ist der Score null: 0 % waere eine Behauptung, 100 % ein Freifahrtschein.
      const score = kannDefined === 0 ? 1 : kannCount === 0 ? null : kannSum / kannCount;
      const rawSuitability = member.aiAssessment?.suitability;
      const suitabilityScore =
        typeof rawSuitability === "number" && Number.isFinite(rawSuitability)
          ? Math.max(0, Math.min(100, rawSuitability)) / 100
          : null;
      const powerFit = powerFitById?.get(member.personId) ?? null;
      const teamFit = teamFitById?.get(member.personId) ?? null;
      // Unterste Gruppe speist sich aus zwei Wegen: Muss-Gate gescheitert ODER Achsen-Score unter
      // der C-Schwelle. `qualified` bleibt Muss-only, damit die Anzeige (% vs. erfuellt/gesamt)
      // unterscheidbar bleibt.
      let cluster: RankCluster;
      if (qualification === "UNQUALIFIED") {
        // Nachgewiesene Muss-Verletzung ist entschieden - sie gehoert auch dann nicht in
        // "Zu pruefen", wenn mangels bewertbarer Kann-Kriterien kein Score existiert
        // (Review spark!190). Das Gate steht deshalb vor dem score-null-Check.
        cluster = "UNQUALIFIED";
      } else if (qualification === "REVIEW_REQUIRED" || score === null || (sortBy === "team" && teamFit === null)) {
        cluster = "REVIEW";
      } else {
        // Die A/B/C-Schwellen clustern ueber die gewaehlte Sortier-Achse, damit Band und
        // Reihenfolge dieselbe Groesse meinen (92 % Smycles-Score bei Schwelle A=80 landet in A).
        // Fehlt der Achsen-Wert (AI aus, keine Powers), faellt die Clusterung wie die Sortierung
        // auf den Anforderungs-Fit zurueck. powerFit ist 0..100, Schwellen sind 0..1. score ist
        // hier bereits als number gesichert (score === null oben abgefangen), daher ist jeder
        // Zweig number.
        const axisScore =
          sortBy === "team" ? (teamFit ?? 0) / 100 :
          sortBy === "smycles"
            ? (suitabilityScore ?? score)
            : sortBy === "power"
              ? (powerFit != null ? powerFit / 100 : score)
              : score;
        if (axisScore < (thresholds.c ?? 0)) cluster = "UNQUALIFIED";
        else if (axisScore >= thresholds.a) cluster = "A";
        else if (axisScore >= thresholds.b) cluster = "B";
        else cluster = "C";
      }
      return {
        personId: member.personId,
        personName: personById.get(member.personId)?.name ?? member.personId,
        cluster,
        score,
        qualification,
        qualified: qualification === "QUALIFIED",
        metCount,
        totalCount: member.items.length,
        mustMet,
        mustTotal,
        kannSum,
        kannTotal: kannCount,
        kannDefined,
        criteria,
        powerFit,
        ...(teamFitById ? { teamFit } : {}),
        suitabilityScore,
      };
    })
    .sort((x, y) => {
      // Muss-Gate bleibt in allen Achsen das erste Ordnungskriterium: qualifiziert vor
      // "zu pruefen" vor nicht qualifiziert (spark#175).
      const byQualification = qualificationRank(x.qualification) - qualificationRank(y.qualification);
      if (byQualification !== 0) return byQualification;
      if (sortBy === "smycles") {
        const d = (y.suitabilityScore ?? -1) - (x.suitabilityScore ?? -1);
        if (d !== 0) return d;
        // gleichauf oder keine Eignung -> nach Anforderungs-Score sortieren
      }
      if (sortBy === "team") return (y.teamFit ?? -1) - (x.teamFit ?? -1);
      if (sortBy === "power") return (y.powerFit ?? -1) - (x.powerFit ?? -1);
      // Score absteigend, nicht bewertbare (null) ans Ende der jeweiligen Gruppe.
      const byScore = (y.score ?? -1) - (x.score ?? -1);
      if (byScore !== 0) return byScore;
      // Gleicher Score -> die belastbarere Bewertung zuerst. Abdeckung ist bewusst nur
      // Tiebreak und wird nie in den Score multipliziert (@adr SPARK-0030-match-result-scoring-atom-derived@1).
      return y.kannTotal - x.kannTotal;
    });
}

function extractCondition(criterion: unknown): unknown {
  if (criterion && typeof criterion === "object" && "condition" in criterion) {
    return (criterion as { condition?: unknown }).condition;
  }
  return criterion;
}

function conditionKey(item: FactSkillItemView): string {
  // Stabiler genesis-Schluessel bevorzugt (@adr SPARK-0030-match-result-scoring-atom-derived@1); Fallback auf serialisierte Condition
  // fuer Alt-Blobs, die conditionKey noch nicht mitliefern.
  const crit = item.criterion as { conditionKey?: unknown } | null;
  if (crit && typeof crit === "object" && typeof crit.conditionKey === "string" && crit.conditionKey.length > 0) {
    return crit.conditionKey;
  }
  const condition = extractCondition(item.criterion);
  return condition === undefined ? "undefined" : JSON.stringify(condition);
}

function shortenRequirementLabel(raw: string): string {
  const match = raw.match(/POWER:([A-Z]+)/);
  if (match && match[1]) return match[1];
  return raw.slice(0, 6).toUpperCase();
}

// Meta-Analyse ueber das Pruefen-Ergebnis: welche Anforderungen erfuellen die meisten,
// und welches Muss-Kriterium schliesst am haeufigsten aus. Genesis liefert die
// Kriterienlisten pro Bewerber in gleicher Reihenfolge; die Aggregation verlaesst
// sich aber nicht darauf, sondern ordnet Items pro Bewerber ueber den Condition-Key
// zu. Fehlt ein Item bei einem Bewerber, zaehlt es als nicht erfuellt.
export interface RankingInsight {
  condition: unknown;
  requirement: string;
  count: number;
  total: number;
}

export interface RankingCriterionInsight {
  condition: unknown;
  requirement: string;
  metCount: number;
  failedCount: number;
  partialCount: number;
  // Nicht bewertbare Items (classification UNKNOWN, spark#175). Sie stecken weder in
  // metCount noch in failedCount und verwaessern averageFulfillment nicht:
  // metCount + failedCount + unknownCount = total.
  unknownCount: number;
  // aiCount zaehlt alle Items mit AI-Begruendung; aiMetCount nur die davon,
  // die als erfuellt zaehlen (AI hat den Hard-Check von 0 auf 1 gekippt).
  aiCount: number;
  aiMetCount: number;
  missingActualCount: number;
  expectedActualCount: number;
  averageFulfillment: number;
  total: number;
  distribution: RankingActualDistribution | null;
}

export interface RankingPoolInsights {
  total: number;
  counts: Record<RankCluster, number>;
  averageScore: number | null;
  medianScore: number | null;
  topGap: number | null;
  qualifiedCount: number;
}

export type RankingActualDistribution =
  | {
      kind: "numeric";
      condition: unknown;
      present: number;
      missing: number;
      min: number;
      average: number;
      median: number;
      standardDeviation: number;
      max: number;
    }
  | {
      kind: "text";
      condition: unknown;
      present: number;
      missing: number;
      topValue: string;
      topCount: number;
      distinctCount: number;
    };

export interface RankingAiCriterionInsight {
  condition: unknown;
  reasonCount: number;
  candidateCount: number;
}

export interface RankingAiInsights {
  reasonCount: number;
  candidateCount: number;
  criteria: RankingAiCriterionInsight[];
  totalItems: number;
}

export interface RankingDataQualityInsights {
  missingActualCount: number;
  expectedActualCount: number;
  topMissing: RankingCriterionInsight | null;
}

export type RankingRecommendation =
  | { type: "must"; criterion: RankingCriterionInsight }
  | { type: "data"; criterion: RankingCriterionInsight }
  | { type: "ai"; reasonCount: number; candidateCount: number }
  | { type: "bottleneck"; criterion: RankingCriterionInsight };

export interface RankingInsights {
  hardestMust: RankingInsight | null;
  pool: RankingPoolInsights;
  // Beste Kriterien nach Erfuellungszaehlung (inkl. AI-Flips): traegt auch bei
  // FULL_AI Information, wo die Ist-Wert-Verteilungen mangels actual leer sind.
  topFulfilled: RankingCriterionInsight[];
  bottlenecks: RankingCriterionInsight[];
  // Alle Kriterien, in Reihenfolge der Referenz-Items. topFulfilled und bottlenecks sind nur
  // zwei Sichten mit je drei Zeilen darauf - der PDF-Bericht zeigt die ganze Liste.
  criteria: RankingCriterionInsight[];
  ai: RankingAiInsights;
  dataQuality: RankingDataQualityInsights;
  recommendation: RankingRecommendation | null;
}

const EMPTY_POOL: RankingPoolInsights = {
  total: 0,
  counts: { A: 0, B: 0, C: 0, REVIEW: 0, UNQUALIFIED: 0 },
  averageScore: null,
  medianScore: null,
  topGap: null,
  qualifiedCount: 0,
};

const EMPTY_INSIGHTS: RankingInsights = {
  hardestMust: null,
  pool: EMPTY_POOL,
  topFulfilled: [],
  bottlenecks: [],
  criteria: [],
  ai: { reasonCount: 0, candidateCount: 0, criteria: [], totalItems: 0 },
  dataQuality: { missingActualCount: 0, expectedActualCount: 0, topMissing: null },
  recommendation: null,
};

export function buildRankingInsights(
  factSkill: FactSkillView | null,
  ranked: RankedCandidate[] = [],
): RankingInsights {
  if (!factSkill || factSkill.teamMembers.length === 0) {
    return EMPTY_INSIGHTS;
  }
  const members = factSkill.teamMembers;
  const total = members.length;
  const referenceItems = members[0]?.items ?? [];
  // Items pro Bewerber nach Condition-Key gruppiert (FIFO bei doppelten
  // Kriterien), damit die Zuordnung nicht von der Listen-Reihenfolge abhaengt.
  const itemsByCondition = members.map((member) => {
    const byKey = new Map<string, FactSkillItemView[]>();
    for (const item of member.items) {
      const key = conditionKey(item);
      const queue = byKey.get(key);
      if (queue) queue.push(item);
      else byKey.set(key, [item]);
    }
    return byKey;
  });
  let hardestMust: RankingInsight | null = null;
  const criterionInsights: RankingCriterionInsight[] = [];
  const aiCandidates = new Set<string>();
  const aiCriteria: RankingAiCriterionInsight[] = [];
  let reasonCount = 0;
  let missingActualCount = 0;
  let expectedActualCount = 0;
  let totalItems = 0;
  for (const first of referenceItems) {
    const requirement = itemRequirement(first);
    const condition = extractCondition(first.criterion);
    const key = conditionKey(first);
    const expectsActual = expectsActualValue(condition);
    let met = 0;
    let partial = 0;
    let failed = 0;
    let unknown = 0;
    let fulfillmentSum = 0;
    let aiCount = 0;
    let aiMetCount = 0;
    let missingForCriterion = 0;
    let expectedForCriterion = 0;
    const aiCandidateIds = new Set<string>();
    const actuals: string[] = [];
    for (const [memberIndex, member] of members.entries()) {
      const item = itemsByCondition[memberIndex]?.get(key)?.shift();
      totalItems += item ? 1 : 0;
      const hasAiReason = Boolean(item?.aiReason?.trim());
      // UNKNOWN wuerde im matched-Fallback von itemFulfillment als 0 (= gescheitert)
      // zaehlen; fehlende Information ist aber kein Scheitern (spark#175). Fehlende
      // Items bleiben dagegen bewusst "nicht erfuellt" (siehe Funktionskommentar).
      const fulfillment = item && !isAssessable(item) ? null : item ? itemFulfillment(item) : 0;
      if (fulfillment == null) {
        unknown += 1;
      } else {
        fulfillmentSum += fulfillment;
        if (fulfillment >= 1) met += 1;
        else {
          failed += 1;
          if (fulfillment > 0) partial += 1;
        }
      }
      if (hasAiReason) {
        reasonCount += 1;
        aiCount += 1;
        if (fulfillment != null && fulfillment >= 1) aiMetCount += 1;
        aiCandidates.add(member.personId);
        aiCandidateIds.add(member.personId);
      }
      if (expectsActual && item && !hasAiReason) {
        expectedActualCount += 1;
        expectedForCriterion += 1;
      }
      if (item?.actual != null && String(item.actual).trim().length > 0) {
        actuals.push(String(item.actual));
      } else if (expectsActual && item && !hasAiReason) {
        missingActualCount += 1;
        missingForCriterion += 1;
      }
    }
    if (requirement === "MUST" && failed > 0 && (hardestMust === null || failed > hardestMust.count)) {
      hardestMust = { condition, requirement, count: failed, total };
    }
    // Mittel nur ueber bewertete Items: unknown im Nenner wuerde den Schnitt genauso
    // druecken wie das fruehere 0-Werten im Zaehler.
    const assessedCount = total - unknown;
    const criterionInsight: RankingCriterionInsight = {
      condition,
      requirement,
      metCount: met,
      failedCount: failed,
      partialCount: partial,
      unknownCount: unknown,
      aiCount,
      aiMetCount,
      missingActualCount: missingForCriterion,
      expectedActualCount: expectedForCriterion,
      averageFulfillment: assessedCount > 0 ? fulfillmentSum / assessedCount : 0,
      total,
      distribution: buildActualDistribution(condition, actuals, total),
    };
    criterionInsights.push(criterionInsight);
    if (aiCount > 0) {
      aiCriteria.push({ condition, reasonCount: aiCount, candidateCount: aiCandidateIds.size });
    }
  }
  const topFulfilled = criterionInsights
    .filter((c) => c.metCount > 0)
    .sort((a, b) => {
      if (a.metCount !== b.metCount) return b.metCount - a.metCount;
      return b.averageFulfillment - a.averageFulfillment;
    })
    .slice(0, 3);
  const bottlenecks = criterionInsights
    // Nur nachgewiesene Schwaechen: metCount < total wuerde jedes Kriterium mit einem
    // unbewertbaren Item zum Engpass erklaeren.
    .filter((c) => c.failedCount > 0)
    .sort((a, b) => {
      if (a.averageFulfillment !== b.averageFulfillment) return a.averageFulfillment - b.averageFulfillment;
      return b.failedCount - a.failedCount;
    })
    .slice(0, 3);
  const topMissing = [...criterionInsights]
    .filter((c) => c.missingActualCount > 0)
    .sort((a, b) => b.missingActualCount - a.missingActualCount)[0] ?? null;
  const topAiCriteria = aiCriteria.sort((a, b) => b.reasonCount - a.reasonCount).slice(0, 3);
  const pool = buildPoolInsights(ranked);
  return {
    hardestMust,
    pool,
    topFulfilled,
    bottlenecks,
    criteria: criterionInsights,
    ai: {
      reasonCount,
      candidateCount: aiCandidates.size,
      criteria: topAiCriteria,
      totalItems,
    },
    dataQuality: { missingActualCount, expectedActualCount, topMissing },
    recommendation: chooseRankingRecommendation({
      hardestMustInsight: criterionInsights
        .filter((c) => c.requirement === "MUST" && c.failedCount > 0)
        .sort((a, b) => b.failedCount - a.failedCount)[0] ?? null,
      topMissing,
      aiReasonCount: reasonCount,
      aiCandidateCount: aiCandidates.size,
      candidateTotal: total,
      bottleneck: bottlenecks[0] ?? null,
    }),
  };
}

function buildPoolInsights(ranked: RankedCandidate[]): RankingPoolInsights {
  if (ranked.length === 0) return EMPTY_POOL;
  const counts: Record<RankCluster, number> = { A: 0, B: 0, C: 0, REVIEW: 0, UNQUALIFIED: 0 };
  for (const candidate of ranked) counts[candidate.cluster] += 1;
  const qualified = ranked.filter((c) => c.qualification === "QUALIFIED");
  // Pool-Kennzahlen nur aus bewertbaren Scores: ein nicht bewertbarer Kandidat darf den
  // Durchschnitt weder senken noch heben (spark#175).
  const scores = qualified
    .map((c) => c.score)
    .filter((s): s is number => s != null)
    .sort((a, b) => b - a);
  return {
    total: ranked.length,
    counts,
    averageScore: scores.length ? scores.reduce((sum, value) => sum + value, 0) / scores.length : null,
    medianScore: median(scores),
    topGap: scores.length >= 3 ? scores[0]! - scores[2]! : null,
    qualifiedCount: qualified.length,
  };
}

function buildActualDistribution(
  condition: unknown,
  actuals: string[],
  total: number,
): RankingActualDistribution | null {
  if (actuals.length === 0 || suppressActualDistribution(condition)) return null;
  if (isNumericActualCondition(condition)) {
    let values = actuals.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
    // Fachwissen-Stufe 0 heisst "unbewertet", nicht "Level 0" (genesis persistiert bewertete
    // Level als ~11..100). Unbewertete verfaelschen Median/Ø als Nullen-Schwemme -> raus.
    // Bei Jahren (Experience/Industry) ist 0 dagegen ein echter Wert und bleibt drin.
    if (conditionTypename(condition) === "ExpertiseCondition") {
      values = values.filter((v) => v > 0);
    }
    // Ein Einzelwert ist keine Verteilung: Median/Ø/SD ueber n=1 suggerieren eine
    // Pool-Aussage, die es nicht gibt. Dann lieber keine Achse (Balken bleibt).
    if (values.length < 2) return null;
    const average = values.reduce((sum, value) => sum + value, 0) / values.length;
    return {
      kind: "numeric",
      condition,
      present: values.length,
      missing: Math.max(0, total - values.length),
      min: values[0]!,
      average,
      median: median(values) ?? values[0]!,
      standardDeviation: standardDeviation(values, average),
      max: values[values.length - 1]!,
    };
  }
  const counts = new Map<string, number>();
  for (const actual of actuals) counts.set(actual, (counts.get(actual) ?? 0) + 1);
  const [topValue, topCount] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0] ?? ["", 0];
  if (!topValue) return null;
  return {
    kind: "text",
    condition,
    present: actuals.length,
    missing: Math.max(0, total - actuals.length),
    topValue,
    topCount,
    distinctCount: counts.size,
  };
}

function isNumericActualCondition(condition: unknown): boolean {
  const typename = conditionTypename(condition);
  return typename === "IndustryExperienceCondition" ||
    typename === "ExperienceCondition" ||
    typename === "ExpertiseCondition";
}

function suppressActualDistribution(condition: unknown): boolean {
  const typename = conditionTypename(condition);
  return typename === "GenderCondition" ||
    typename === "AgeCondition" ||
    typename === "ChildrenCountCondition" ||
    typename === "MaritalCondition" ||
    typename === "CitizenshipCondition" ||
    typename === "NationalityCondition";
}

function expectsActualValue(condition: unknown): boolean {
  const typename = conditionTypename(condition);
  return typename === "AgeCondition" ||
    typename === "AnnualSalaryCondition" ||
    typename === "CertificateCondition" ||
    typename === "ChildrenCountCondition" ||
    typename === "CitizenshipCondition" ||
    typename === "CityCondition" ||
    typename === "CountryCondition" ||
    typename === "DriversLicenseCondition" ||
    typename === "EducationCondition" ||
    typename === "ExperienceCondition" ||
    typename === "ExpertiseCondition" ||
    typename === "GenderCondition" ||
    typename === "IndustryExperienceCondition" ||
    typename === "JobDepartmentCondition" ||
    typename === "JobLocationCondition" ||
    typename === "LanguageCondition" ||
    typename === "MaritalCondition" ||
    typename === "NationalityCondition";
}

export function conditionTypename(condition: unknown): string {
  return condition && typeof condition === "object" && typeof (condition as { __typename?: unknown }).__typename === "string"
    ? ((condition as { __typename: string }).__typename)
    : "";
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid]!;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

function standardDeviation(values: number[], average: number): number {
  if (values.length <= 1) return 0;
  const variance = values.reduce((sum, value) => sum + (value - average) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

function chooseRankingRecommendation({
  hardestMustInsight,
  topMissing,
  aiReasonCount,
  aiCandidateCount,
  candidateTotal,
  bottleneck,
}: {
  hardestMustInsight: RankingCriterionInsight | null;
  topMissing: RankingCriterionInsight | null;
  aiReasonCount: number;
  aiCandidateCount: number;
  candidateTotal: number;
  bottleneck: RankingCriterionInsight | null;
}): RankingRecommendation | null {
  if (hardestMustInsight && hardestMustInsight.failedCount > 0) {
    return { type: "must", criterion: hardestMustInsight };
  }
  if (topMissing && topMissing.missingActualCount / Math.max(1, topMissing.total) >= 0.4) {
    return { type: "data", criterion: topMissing };
  }
  if (aiReasonCount > 0 && aiCandidateCount / Math.max(1, candidateTotal) >= 0.4) {
    return { type: "ai", reasonCount: aiReasonCount, candidateCount: aiCandidateCount };
  }
  if (bottleneck && bottleneck.averageFulfillment < 0.75) {
    return { type: "bottleneck", criterion: bottleneck };
  }
  return null;
}

// Umkreis-Insight (Radius-Karte in der Match-Analyse). Die Distanz steht nur im deterministisch
// von genesis formatierten Begruendungssatz ("Distanz rund 21 km, ..."), ein eigenes Feld im
// Result-Blob gibt es (noch) nicht; das Satzformat besitzen wir auf beiden Seiten.
const RADIUS_REASON_PATTERN = /^(?:Distanz rund|Distance around) (\d+(?:[.,]\d+)?) km/;

export interface RadiusInsightPoint {
  personId: string;
  personName: string;
  distanceKm: number;
  inside: boolean;
}

export interface RadiusInsight {
  city: string;
  radiusKm: number;
  // Aufsteigend nach Distanz sortiert.
  points: RadiusInsightPoint[];
  // Kandidaten mit Radius-Kriterium, aber ohne parsebare Distanz (GeoNames-Fallback auf KI).
  missing: { personId: string; personName: string }[];
}

function radiusCondition(condition: unknown): { city: string; radiusKm: number } | null {
  const cond = condition as { __typename?: unknown; value?: unknown; radiusKm?: unknown } | null;
  if (
    cond
    && cond.__typename === "CityCondition"
    && typeof cond.value === "string"
    && typeof cond.radiusKm === "number"
    && cond.radiusKm > 0
  ) {
    return { city: cond.value, radiusKm: cond.radiusKm };
  }
  return null;
}

// Kategorie eines Kriteriums als Message-id plus Sortierrang. Lag bis hierher in
// CandidateRankingPanel; der PDF-Bericht braucht dieselbe Gruppierung, und eine 1500-Zeilen-
// DOM-Komponente ist kein Ort, aus dem ein Renderer-naher Pfad importiert
// (@adr SPARK-0044-match-result-pdfs-use-a-dedicated-spa-renderer@3).
export function criterionKindId(condition: unknown): string | null {
  if (!condition || typeof condition !== "object") return null;
  const obj = condition as { __typename?: unknown; name?: unknown };
  const typename = typeof obj.__typename === "string" ? obj.__typename : "";
  if (!typename && typeof obj.name === "string" && obj.name.startsWith("POWER:")) {
    return "match.result.ranking.kindPowers";
  }
  switch (typename) {
    case "ExpertiseCondition":
      return "match.result.ranking.kindExpertise";
    case "EducationCondition":
      return "match.result.ranking.kindEducation";
    case "CertificateCondition":
      return "match.result.ranking.kindCertificate";
    case "ExperienceCondition":
    case "IndustryExperienceCondition":
      return "match.result.ranking.kindExperience";
    case "LanguageCondition":
      return "match.result.ranking.kindLanguage";
    case "AgeCondition":
    case "AnnualSalaryCondition":
    case "ChildrenCountCondition":
    case "CitizenshipCondition":
    case "CityCondition":
    case "CountryCondition":
    case "DriversLicenseCondition":
    case "GenderCondition":
    case "JobDepartmentCondition":
    case "JobLocationCondition":
    case "MaritalCondition":
    case "NationalityCondition":
      return "match.result.ranking.kindBasics";
    default:
      return null;
  }
}

// Reihenfolge der Kategorie-Gruppen analog zum Challenge-Editor
// (Personen-Details -> Berufserfahrung -> Ausbildung/Qualifikationen -> Fachwissen).
export function criterionKindOrder(condition: unknown): number {
  if (!condition || typeof condition !== "object") return 90;
  const obj = condition as { __typename?: unknown; name?: unknown };
  const typename = typeof obj.__typename === "string" ? obj.__typename : "";
  if (!typename && typeof obj.name === "string" && obj.name.startsWith("POWER:")) return 6;
  switch (typename) {
    case "AgeCondition":
    case "AnnualSalaryCondition":
    case "ChildrenCountCondition":
    case "CitizenshipCondition":
    case "CityCondition":
    case "CountryCondition":
    case "DriversLicenseCondition":
    case "GenderCondition":
    case "JobDepartmentCondition":
    case "JobLocationCondition":
    case "MaritalCondition":
    case "NationalityCondition":
      return 0;
    case "ExperienceCondition":
    case "IndustryExperienceCondition":
      return 1;
    case "EducationCondition":
      return 2;
    case "CertificateCondition":
      return 3;
    case "LanguageCondition":
      return 4;
    case "ExpertiseCondition":
      return 5;
    default:
      return 90;
  }
}

export function buildRadiusInsight(ranked: RankedCandidate[]): RadiusInsight | null {
  let target: { city: string; radiusKm: number } | null = null;
  for (const candidate of ranked) {
    for (const criterion of candidate.criteria) {
      target = radiusCondition(criterion.condition);
      if (target) break;
    }
    if (target) break;
  }
  if (!target) return null;
  const { city, radiusKm } = target;
  const points: RadiusInsightPoint[] = [];
  const missing: { personId: string; personName: string }[] = [];
  for (const candidate of ranked) {
    const criterion = candidate.criteria.find((item) => {
      const cond = radiusCondition(item.condition);
      return cond?.city === city && cond?.radiusKm === radiusKm;
    });
    if (!criterion) continue;
    const distanceRaw = criterion.aiReason?.match(RADIUS_REASON_PATTERN)?.[1];
    if (!distanceRaw) {
      missing.push({ personId: candidate.personId, personName: candidate.personName });
      continue;
    }
    const distanceKm = Number(distanceRaw.replace(",", "."));
    points.push({
      personId: candidate.personId,
      personName: candidate.personName,
      distanceKm,
      inside: distanceKm <= radiusKm,
    });
  }
  if (points.length === 0) return null;
  points.sort((a, b) => a.distanceKm - b.distanceKm);
  return { city, radiusKm, points, missing };
}


// ─── Team-Mitglied im Match (spark #236) ─────────────────────────────────────

export interface MemberRequirementTally {
  met: number;
  total: number;
  mustMet: number;
  mustTotal: number;
  // Nicht bewertbar (UNKNOWN): bleibt aus met heraus, sonst zaehlt "unbekannt" wie "nein".
  unrated: number;
  // Median der Deckung im eigenen Team. Ohne ihn ist "deckt 6 von 9" nicht einzuordnen: 6 kann
  // die Spitze des Teams oder sein Schlusslicht sein. Median statt Mittel, weil ein einzelner
  // Kandidat mit 0 Treffern das Mittel verzieht. null bei Teams unter zwei Mitgliedern - dann
  // gibt es keinen Vergleich, nur die Person.
  teamMedianMet: number | null;
}

// Erfuellte Kriterien einer Person. UNKNOWN zaehlt nicht als erfuellt (und nicht als verfehlt).
function memberMetCount(member: FactSkillMemberView): number {
  let met = 0;
  for (const item of member.items) {
    if (isAssessable(item) && itemFulfillment(item) >= 1) met += 1;
  }
  return met;
}

export interface MemberDetail {
  requirements: MemberRequirementTally;
  // Anforderungen, die das Team HEUTE erfuellt und ohne diese Person nicht mehr:
  // die eine Aussage, die "5 von 7" nicht macht - was zerbricht, wenn ich sie tausche.
  criticalFor: unknown[];
  criticalMustFor: unknown[];
  // Team-relative Superlative. Alle drei sind Aussagen, die erst im Vergleich entstehen und
  // deshalb hier berechnet werden - eine Person allein weiss nicht, ob sie die meiste Deckung
  // bringt. Gleichstand teilen sich alle Beteiligten.
  topCoverage: boolean;
  noCoverage: boolean;
  topPowerFit: boolean;
  // Cluster-Mittel dieser Person, null wo sie nichts eingeschaetzt hat, plus die
  // Zaehlung starker Powers im Cluster - das Mittel unterscheidet "5x68" nicht von
  // "{91,87,51,55,59}", die Zaehlung schon (Whitepaper Kap. 6.1.3).
  // teamScore = Cluster-Mittel des GANZEN Teams. Die Person wird gegen ihr eigenes Team
  // gelesen (Mitglied-im-Team, vergleichend), nicht absolut - absolut ist das Profil.
  clusters: Array<{
    key: PowerGroupKey;
    score: number | null;
    teamScore: number | null;
    strong: number;
    assessed: number;
  }>;
  // Die einzelnen Power-Werte dieser Person (Elementname, Cluster, Wert), null wo sie diese
  // Power nicht eingeschaetzt hat. Treibt den Person-Power-Fit-Tab (spark #257): dessen
  // 30-Achsen-Radar und Drilldown brauchen die Elementebene, nicht nur die Cluster-Mittel oben.
  cells: HeatmapCell[];
  // Cluster, die diese Person traegt, und die, die sie als EINZIGE traegt (SPOF).
  anchors: PowerGroupKey[];
  soleAnchors: PowerGroupKey[];
  // Neigung DIESER Person, mit demselben Rauschtest wie beim Team: bei n=30 Powers
  // nennt er meist "ausgeglichen" - das ist die ehrliche Antwort, keine fehlende.
  tilts: PowerTilt[];
  // Die Kriterien dieser Person mit Nachweis, damit die Hard-Skill-Seite nicht nur als
  // Zahl ("5 von 7") dasteht. critical = faellt ohne sie team-weit weg.
  criteria: Array<{
    condition: unknown;
    requirement: string;
    fulfillment: number | null;
    actual: string | null;
    // Begruendung des Modells fuer DIESES Kriterium, falls die Condition mit AI ausgewertet wurde
    // (genesis: aiMode != NO_AI, pro Condition — nicht pro Modus). Steht damit auch in
    // Optimize/Assess im Blob, nicht nur in RANK.
    aiReason: string | null;
    assessable: boolean;
    critical: boolean;
  }>;
  // Mit den Team-Powers-Wuenschen gewichtetes Mittel ("passt die Person zum Wunsch").
  powerFit: number | null;
  aiAssessment: AiAssessmentView | null;
  // Fachkompetenzen, die diese Person als EINZIGE stark mitbringt (@adr SPARK-0025-i18n-bundle-single-flat-representation@1, nicht
  // die Anforderungsliste). Das Gegenstueck zu criticalFor: nicht "was zerbricht am Match",
  // sondern "was ist an dieser Person einzigartig" - auch wenn niemand danach gefragt hat.
  exclusiveSkills: string[];
  exclusiveRequestedSkills: string[];
}

// Alles, was ueber EIN Team-Mitglied in DIESEM Match bekannt ist - aus den Blob-Atomen,
// kein zusaetzlicher Request. null, wenn die Person in keinem der Teilergebnisse steckt.
export function buildMemberDetail(
  factSkill: FactSkillView | null,
  powers: PowersView | null,
  personId: string,
  elementWeights?: Readonly<Record<string, number>>,
): MemberDetail | null {
  const fsMember = factSkill?.teamMembers.find((m) => m.personId === personId) ?? null;
  const pMember = powers?.teamMembers.find((m) => m.personId === personId) ?? null;
  if (!fsMember && !pMember) return null;

  // Median ueber das GANZE Team, inklusive dieser Person. Vorher wurde die Person
  // herausgerechnet ("Median der anderen") - dabei bewegt sich der Wert systematisch von der
  // lesenden Person WEG: wer viel deckt, sieht eine niedrige Latte, wer wenig deckt, eine
  // hohe. Auf echten Daten sah dasselbe Team fuer den Staerksten 2,5 und fuer die
  // Schwaechsten 3,5. Ein als "Team-Median" beschrifteter Wert muss ausserdem fuer alle
  // gleich sein, sonst vergleichen zwei Kollegen zwei verschiedene Latten.
  const allMetCounts = (factSkill?.teamMembers ?? []).map(memberMetCount);
  const requirements: MemberRequirementTally = {
    met: 0,
    total: fsMember?.items.length ?? 0,
    mustMet: 0,
    mustTotal: 0,
    unrated: 0,
    // Ab zwei Personen: bei einem Ein-Personen-Team waere die Latte die Person selbst.
    teamMedianMet: allMetCounts.length > 1 ? median(allMetCounts) : null,
  };
  for (const item of fsMember?.items ?? []) {
    const must = itemRequirement(item) === "MUST";
    if (must) requirements.mustTotal += 1;
    if (!isAssessable(item)) {
      requirements.unrated += 1;
      continue;
    }
    if (itemFulfillment(item) >= 1) {
      requirements.met += 1;
      if (must) requirements.mustMet += 1;
    }
  }

  // Deckungs-Superlative: hoechste Deckung im Team, bzw. gar keine. Traegt das ganze Team
  // dieselbe Zahl, unterscheidet "die meisten" niemanden mehr - dann bleibt die Aussage weg.
  const maxMet = allMetCounts.length > 0 ? Math.max(...allMetCounts) : 0;
  const tiedAtMax = allMetCounts.filter((n) => n === maxMet).length;
  const topCoverage =
    maxMet > 0 && requirements.met === maxMet && tiedAtMax < allMetCounts.length;
  const noCoverage = requirements.total > 0 && requirements.met === 0;
  // Power-Fit ist nur mit Wuenschen definiert; ohne sie gibt es keinen Spitzenreiter.
  const fitById = elementWeights ? buildPowerFit(powers, elementWeights) : new Map<string, number>();
  const fits = [...fitById.values()];
  const myFit = fitById.get(personId);
  const topPowerFit =
    myFit != null && fits.length > 1 && myFit === Math.max(...fits) &&
    fits.filter((f) => f === myFit).length < fits.length;

  const criticalIndexes = criticalCriterionIndexes(factSkill, personId);
  const criticalIndexList = [...criticalIndexes].sort((a, b) => a - b);
  const criticalFor: unknown[] = criticalIndexList.map((idx) => factSkill?.criteria[idx]?.condition);
  // Getrennt gefuehrt, weil "ohne diese Person faellt das Muss" die einzige Aussage im Panel
  // ist, die eine Entscheidung erzwingt - sie darf nicht mit einem Kann in einem Satz landen.
  const criticalMustFor: unknown[] = criticalIndexList
    .filter((idx) => {
      const first = factSkill?.teamMembers[0]?.items[idx];
      return first
        ? itemRequirement(first) === "MUST"
        : (factSkill?.criteria[idx]?.condition as { requirement?: unknown } | null)?.requirement ===
            "MUST";
    })
    .map((idx) => factSkill?.criteria[idx]?.condition);

  const soleStrong = buildSkillCoverage(factSkill).filter(
    (c) => c.strong === 1 && c.strongPersonIds[0] === personId,
  );
  const exclusiveSkills = soleStrong.map((c) => c.skill);
  // Dieselbe Liste, aber nur die GEFRAGTEN Kompetenzen. "Einzige mit Figma" ist fuer
  // behalten-oder-tauschen belanglos, wenn Figma nie verlangt war.
  const exclusiveRequestedSkills = soleStrong.filter((c) => c.requested).map((c) => c.skill);

  const anchor = buildMemberAnchors(powers).find((a) => a.personId === personId) ?? null;
  const teamCells: HeatmapCell[] = (powers?.teamMembers ?? []).flatMap((m) =>
    m.items.map((i) => ({ element: i.criterion.element, group: i.criterion.group, value: i.value })),
  );

  const cells: HeatmapCell[] = (pMember?.items ?? []).map((i) => ({
    element: i.criterion.element,
    group: i.criterion.group,
    value: i.value,
  }));
  const criteria = (fsMember?.items ?? []).map((item, idx) => ({
    condition: extractCondition(item.criterion),
    requirement: itemRequirement(item),
    fulfillment: isAssessable(item) ? itemFulfillment(item) : null,
    actual: item.actual ?? null,
    aiReason: item.aiReason?.trim() ? item.aiReason : null,
    assessable: isAssessable(item),
    critical: criticalIndexes.has(idx),
  }));
  // Muss vor Kann, innerhalb dessen offen vor erfuellt: was Aufmerksamkeit braucht, zuerst.
  criteria.sort(
    (a, b) =>
      Number(b.requirement === "MUST") - Number(a.requirement === "MUST") ||
      (a.fulfillment ?? 0) - (b.fulfillment ?? 0),
  );

  return {
    requirements,
    criticalFor,
    criticalMustFor,
    topCoverage,
    noCoverage,
    topPowerFit,
    criteria,
    tilts: pMember ? buildPowerTilts({ ...(powers as PowersView), teamMembers: [pMember] }) : [],
    cells,
    anchors: anchor?.anchors ?? [],
    soleAnchors: anchor?.soleAnchors ?? [],
    clusters: CLUSTER_ORDER.map((key) => {
      const inCluster = cells.filter((c) => c.group === key && c.value !== null);
      return {
        key,
        score: clusterMean(cells, key),
        teamScore: clusterMean(teamCells, key),
        strong: inCluster.filter((c) => (c.value as number) > POWER_STRONG_THRESHOLD).length,
        assessed: inCluster.length,
      };
    }),
    powerFit: elementWeights ? (buildPowerFit(powers, elementWeights).get(personId) ?? null) : null,
    aiAssessment: fsMember?.aiAssessment ?? null,
    exclusiveSkills,
    exclusiveRequestedSkills,
  };
}

// ─── Profilstruktur: Schwellwert-Zaehlungen statt Mittelwerte (spark #236) ───
//
// Warum Zaehlungen: die Element-Werte streuen real (30..86, sd ~10), aber jede
// Mittelung darueber vernichtet die Streuung - Cluster-Mittel liegen ueber echte Teams
// hinweg nur 3..7 Punkte auseinander, Personen-Mittel 1..2. Alles, was UNTER dem Mittel
// zaehlt, unterscheidet Teams noch; alles, was darauf aufbaut, nicht mehr. Deshalb hier
// Schwellwert-Zaehlungen (Whitepaper Kap. 6.1.3 "gestaffelter Cluster-Bonus" und
// Kap. 7.8 "Booster-Luecken-Analyse") und ausdruecklich KEINE weiteren Mittelwerte.

// Kalibrier-Stellschraube aus Kap. 6.1.3. 65 = Standard; hoeher macht die Aussage
// strenger. Bewusst ein Wert, nicht mehrere Baender - die Baender 0-19/20-39/...
// trennen bei unseren Daten nichts, weil praktisch alles zwischen 60 und 79 liegt.
export const POWER_STRONG_THRESHOLD = 65;
// Ab so vielen starken Powers in einem Cluster gilt eine Person als Traeger des Clusters.
export const CLUSTER_CARRIER_MIN = 3;

// Die Pull-Booster aus Kap. 6.1.1 / 7.1.1, soweit sie in unseren 30 Powers existieren.
// "Willenskraft" ist im Whitepaper Booster, aber keine unserer Powers - deshalb fehlt sie
// hier bewusst, statt sie auf eine aehnliche Power zu biegen.
export const PULL_BOOSTERS = ["COURAGE", "IMAGINATION", "INTENTION", "INSPIRATION", "IMPROVISATION"] as const;

export interface ClusterCarriers {
  key: PowerGroupKey;
  // Personen mit mindestens CLUSTER_CARRIER_MIN starken Powers in diesem Cluster.
  carriers: number;
  // Personen mit vollstaendigem Cluster-Profil (alle 5 stark) - "Signaturstaerke".
  signature: number;
  assessed: number;
}

// Wie viele Personen tragen einen Cluster wirklich - im Unterschied zum Cluster-Mittel,
// das nicht zwischen "5x68" und "{91,87,51,55,59}" unterscheidet.
export function buildClusterCarriers(
  powers: PowersView | null,
  threshold: number = POWER_STRONG_THRESHOLD,
): ClusterCarriers[] {
  if (!powers) return [];
  const groupOf = new Map(powers.criteria.map((c) => [c.element, c.group]));
  return CLUSTER_ORDER.map((key) => {
    let carriers = 0;
    let signature = 0;
    let assessed = 0;
    for (const member of powers.teamMembers) {
      const inCluster = member.items.filter(
        (i) => groupOf.get(i.criterion.element) === key && i.value != null,
      );
      if (inCluster.length === 0) continue;
      assessed += 1;
      const strong = inCluster.filter((i) => (i.value as number) > threshold).length;
      if (strong >= CLUSTER_CARRIER_MIN) carriers += 1;
      if (strong === inCluster.length) signature += 1;
    }
    return { key, carriers, signature, assessed };
  }).filter((c) => c.assessed > 0);
}

export interface MemberAnchor {
  personId: string;
  // Cluster, die diese Person traegt (>= CLUSTER_CARRIER_MIN starke Powers) - ihr Beitrag zum Team.
  anchors: PowerGroupKey[];
  // Cluster, das NUR diese Person traegt: geht sie, faellt das Cluster auf null Traeger (SPOF).
  soleAnchors: PowerGroupKey[];
}

// Wer traegt was - dieselbe Traeger-Definition wie buildClusterCarriers, aber personenweise.
// Beantwortet "wer sind die Schluesselpersonen, wen kann ich nicht verlieren", was die reine
// Traeger-ZAHL nicht sagt (spark #236). Nur bewertete Personen; unbewertete deckt der Datenhinweis.
export function buildMemberAnchors(
  powers: PowersView | null,
  threshold: number = POWER_STRONG_THRESHOLD,
): MemberAnchor[] {
  if (!powers) return [];
  const groupOf = new Map(powers.criteria.map((c) => [c.element, c.group]));
  const perMember = powers.teamMembers
    .map((member) => {
      if (!member.items.some((i) => i.value != null)) return null;
      const anchors = CLUSTER_ORDER.filter((key) => {
        const inCluster = member.items.filter(
          (i) => groupOf.get(i.criterion.element) === key && i.value != null,
        );
        return (
          inCluster.length > 0 &&
          inCluster.filter((i) => (i.value as number) > threshold).length >= CLUSTER_CARRIER_MIN
        );
      });
      return { personId: member.personId, anchors };
    })
    .filter((m): m is { personId: string; anchors: PowerGroupKey[] } => m !== null);
  const carrierCount = new Map<PowerGroupKey, number>();
  for (const m of perMember) for (const key of m.anchors) carrierCount.set(key, (carrierCount.get(key) ?? 0) + 1);
  return perMember.map((m) => ({
    personId: m.personId,
    anchors: m.anchors,
    soleAnchors: m.anchors.filter((key) => carrierCount.get(key) === 1),
  }));
}

// Anforderungen, die das Team heute erfuellt und ohne diese Person nicht mehr. Gleiches
// Composition-Gate, nur ein Team kleiner: MAX-Auflagen koennen dadurch auch erfuellt BLEIBEN,
// deshalb neu rechnen statt "einziger Treffer" zu zaehlen.
function criticalCriterionIndexes(factSkill: FactSkillView | null, personId: string): Set<number> {
  const critical = new Set<number>();
  const member = factSkill?.teamMembers.find((m) => m.personId === personId);
  if (!factSkill || !member) return critical;
  const teamSize = factSkill.teamMembers.length;
  factSkill.criteria.forEach((c, idx) => {
    let matched = 0;
    for (const m of factSkill.teamMembers) if (m.items[idx]?.matched) matched += 1;
    if (!isCriterionFulfilled(c.condition, matched, teamSize)) return;
    const mine = member.items[idx]?.matched ? 1 : 0;
    if (isCriterionFulfilled(c.condition, matched - mine, teamSize - 1)) return;
    critical.add(idx);
  });
  return critical;
}

export interface BoosterCoverage {
  element: string;
  covered: number;
  assessed: number;
}

// Deckung der Pull-Booster im Team: wie viele Mitglieder bringen die Power stark mit.
// Das ist die diagnostische Gegenfrage zum Cluster-Mittel - sie benennt die konkrete
// Luecke (Kap. 7.8) statt sie wegzumitteln.
export function buildBoosterCoverage(
  powers: PowersView | null,
  threshold: number = POWER_STRONG_THRESHOLD,
): BoosterCoverage[] {
  if (!powers) return [];
  return PULL_BOOSTERS.map((element) => {
    const values = powers.teamMembers
      .flatMap((m) => m.items.filter((i) => i.criterion.element === element))
      .map((i) => i.value)
      .filter((v): v is number => v != null);
    return { element, covered: values.filter((v) => v > threshold).length, assessed: values.length };
  }).filter((b) => b.assessed > 0);
}

// ─── Je Element: Ist-Mittel und Zaehlung (spark #236) ───────────────────────
// Eine Stelle fuer beides, weil Radar (Mittel) und Betonungs-Liste (Zaehlung) dieselben
// Atome brauchen. Ohne Zaehlung ist ein Element-Mittel von 68 nicht von {91,87,51,55,59}
// zu unterscheiden - genau die Aussage, die das Cluster-Mittel schon wegrechnet.
export interface ElementStat {
  // Team-Mittel 0-100; null, wenn niemand diese Power selbst eingeschaetzt hat.
  mean: number | null;
  strong: number;
  assessed: number;
  // strong === 1 -> die einzige Person, die die Power stark mitbringt.
  strongPersonIds: string[];
  // Die Einzelwerte, aufsteigend. Das Mittel allein macht {91,87,51,55,59} und 5x68
  // ununterscheidbar; die Zaehlung ueber der Schwelle macht 66 und 91 ununterscheidbar.
  // Die Werte selbst koennen beides zeigen und haengen nicht an der Teamgroesse.
  values: number[];
}

export function buildElementStats(
  powers: PowersView | null,
  threshold: number = POWER_STRONG_THRESHOLD,
): Map<string, ElementStat> {
  const byElement = new Map<string, ElementStat>();
  if (!powers) return byElement;
  for (const c of powers.criteria) {
    let sum = 0;
    const values: number[] = [];
    const strongPersonIds: string[] = [];
    for (const m of powers.teamMembers) {
      for (const i of m.items) {
        if (i.criterion.element !== c.element || i.value == null) continue;
        sum += i.value;
        values.push(i.value);
        if (i.value > threshold) strongPersonIds.push(m.personId);
      }
    }
    const assessed = values.length;
    values.sort((a, b) => a - b);
    byElement.set(c.element, {
      mean: assessed > 0 ? sum / assessed : null,
      strong: strongPersonIds.length,
      assessed,
      strongPersonIds,
      values,
    });
  }
  return byElement;
}

// Die betonten Powers: je Element der Wunsch (0-10) neben Ist-Mittel UND Zaehlung, nach
// Wunsch absteigend. Bewusst KEINE Erfuellung, kein Soll-Strich, kein Haken: ein Gewicht
// ist ein Objective-Term, keine Auflage - es gibt keine Schwelle, die man reissen kann.
// Die Aussage ist "hier hast du Gewicht gelegt, so ist das Team dort gebaut".
// Leer, wenn kein Profil gesetzt ist ODER alle Gewichte gleich sind: eine flache
// Gewichtung (Preset "Ausgewogen") drueckt keine Betonung aus, und eine Liste, die dann
// nach Ist-Wert sortiert, wuerde eine Absicht behaupten, die niemand geaeussert hat.
// EINE Powers-Liste (spark #236). Vorher zwei Kaesten: "Top Powers" (nach Ist-Mittel) und
// "Wichtigste Powers" (nach Wunsch). Beide zeigten dieselbe Zeile - Name, Zaehlung, Mittel -
// und unterschieden sich nur in der Sortierung. Also eine Liste, zwei Sortierungen.
export interface PowerRow extends ElementStat {
  element: string;
  mean: number; // hier nie null: Elemente ohne Einschaetzung stehen nicht in der Liste
  // Wunsch 0-10 aus der Powers Scorecard; null = fuer dieses Element wurde nichts gewichtet.
  wish: number | null;
}

export function buildPowerRows(
  powers: PowersView | null,
  elementWeights?: Readonly<Record<string, number>>,
  threshold: number = POWER_STRONG_THRESHOLD,
): PowerRow[] {
  return Array.from(buildElementStats(powers, threshold))
    .flatMap(([element, stat]) => {
      if (stat.mean == null) return [];
      const raw = elementWeights?.[element];
      return [{ element, ...stat, mean: stat.mean, wish: typeof raw === "number" ? elementWish(raw) : null }];
    })
    .sort((a, b) => b.mean - a.mean || a.element.localeCompare(b.element));
}

// Nach Wunsch absteigend; bei gleichem Wunsch das schwaechere Team-Mittel zuerst, damit die
// duenne Stelle oben steht und nicht hinter einer gleich gewichteten Staerke verschwindet.
// Elemente ohne Wunsch fallen raus: in dieser Sortierung haetten sie keinen Platz.
export function sortPowerRowsByWish(rows: PowerRow[]): PowerRow[] {
  return rows
    .filter((r) => r.wish != null)
    .sort((a, b) => (b.wish ?? 0) - (a.wish ?? 0) || a.mean - b.mean || a.element.localeCompare(b.element));
}

// Ist die Wunsch-Sortierung ueberhaupt eine Aussage? Ohne Gewichte nicht, und bei durchweg
// gleichen Gewichten auch nicht: "alle gleich" heisst, es wurde keine Betonung geaeussert -
// danach zu sortieren wuerde eine Absicht behaupten, die niemand hatte.
export function powerWishesUsable(rows: PowerRow[]): boolean {
  const wishes = rows.flatMap((r) => (r.wish == null ? [] : [r.wish]));
  return wishes.length > 0 && wishes.some((w) => w !== wishes[0]);
}

// ─── Achsen-Neigung: Antrieb/Anziehung und Exploit/Explore (spark #236) ──────
//
// Kap. 4.2: jede Power liegt auf zwei Achsen. Als MITTELWERT ist die Achse nicht
// lesbar - ueber echte Teams liegen Push- und Pull-Mittel 0-4 Punkte auseinander.
// Als ANTEIL starker Powers je Seite dagegen 24 Punkte, mit Neigungen bis +-14.
// Die Normierung auf Anteile ist zwingend: Push umfasst 4 Cluster, Pull 2 - rohe
// Zaehlungen wuerden immer Push gewinnen lassen.
const PUSH_CLUSTERS: PowerGroupKey[] = [
  "RESILIENCE",
  "RESULT_ORIENTATION",
  "INVENTIVENESS",
  "FUTURE_ORIENTATION",
];
const EXPLOIT_CLUSTERS: PowerGroupKey[] = ["RESILIENCE", "RESULT_ORIENTATION"];
const EXPLORE_CLUSTERS: PowerGroupKey[] = ["INVENTIVENESS", "FUTURE_ORIENTATION"];

export type TiltAxis = "DRIVE" | "MINDSET";
export type TiltSide = "PUSH" | "PULL" | "EXPLOIT" | "EXPLORE";
// Abstufung nach Vielfachen des Stichprobenfehlers, nicht nach Punktwerten: die Stufe
// sagt, wie weit die Differenz das Rauschen hinter sich laesst, und skaliert damit
// automatisch mit der Teamgroesse.
export type TiltGrade = "LEANS" | "CLEAR" | "STRONG";
const TILT_GRADE_SIGMA: Array<[TiltGrade, number]> = [
  ["STRONG", 2],
  ["CLEAR", 1.5],
  ["LEANS", 1],
];

// Feste Punkt-Schwellen waeren falsch: die Neigung ist eine Differenz zweier Anteile, ihr
// Stichprobenfehler haengt an der Teamgroesse. Bei 7 Personen liegt er bei 6-8 Prozent-
// punkten - eine "5 Punkte"-Schwelle liegt also UNTER dem Rauschen. Deshalb wird die
// Richtung nur benannt, wenn die Differenz groesser ist als ihr eigener Standardfehler,
// der aus den Daten kommt und mit der Teamgroesse automatisch schrumpft.
// Bewusst 1x SE, nicht 1.96x: das ist eine Tendenz-Angabe, kein Signifikanztest - bei
// realistischen Teamgroessen wuerde ein 95%-Kriterium nie ansprechen.
// Mit Stetigkeitskorrektur (+0.5 je Seite): ohne sie wird der Fehler bei 0 % oder 100 %
// exakt null, und genau die perfekte Trennung - alle Antriebs-Powers stark, keine
// Anziehungs-Power - waere dann "ausgeglichen" statt der deutlichsten Neigung ueberhaupt.
// Die Korrektur betrifft nur den Fehler; die angezeigten Anteile bleiben die rohen.
function shareDiffStandardError(
  strongA: number,
  nA: number,
  strongB: number,
  nB: number,
): number {
  const pa = (strongA + 0.5) / (nA + 1);
  const pb = (strongB + 0.5) / (nB + 1);
  return 100 * Math.sqrt((pa * (1 - pa)) / nA + (pb * (1 - pb)) / nB);
}
// Unter diesem Anteil auf BEIDEN Seiten ist "ausgeglichen" ein Lob, das nicht stimmt:
// dann ist das Team nicht balanciert, sondern beidseitig schwach.
export const TILT_WEAK_SHARE = 50;

export interface PowerTilt {
  axis: TiltAxis;
  // Fuehrende Seite, oder null bei ausgeglichen.
  side: TiltSide | null;
  // Betrag der Neigung in Prozentpunkten (immer >= 0). Bewusst NICHT angezeigt: bei
  // realistischen Teamgroessen ist der Betrag kleiner als sein eigenes 95%-Band und
  // schwankt um mehr als 10 Punkte, wenn man die 65er-Schwelle um 5 verschiebt. Die
  // Richtung dagegen kippt ueber Schwellen 55-75 in keinem echten Team.
  points: number;
  // Stichprobenfehler der Differenz - die Grenze, ab der eine Richtung benannt wird.
  standardError: number;
  // null genau dann, wenn side null ist (Differenz innerhalb des Rauschens).
  grade: TiltGrade | null;
  // Anteile starker Powers je Seite, in der Reihenfolge (PUSH|EXPLOIT, PULL|EXPLORE).
  shares: [number, number];
  // Beide Seiten unter TILT_WEAK_SHARE - Neigung ist dann nicht die Hauptaussage.
  bothWeak: boolean;
}

function strongShare(
  powers: PowersView,
  groups: readonly string[],
  threshold: number,
): { share: number; strong: number; n: number } | null {
  const groupOf = new Map(powers.criteria.map((c) => [c.element, c.group]));
  const values = powers.teamMembers
    .flatMap((m) => m.items)
    .filter((i) => i.value != null && groups.includes(groupOf.get(i.criterion.element) ?? ""))
    .map((i) => i.value as number);
  if (values.length === 0) return null;
  const strong = values.filter((v) => v > threshold).length;
  return { share: (100 * strong) / values.length, strong, n: values.length };
}

// Neigung beider Achsen. Leeres Array, wenn eine Seite gar nicht erfasst ist - eine
// Achse mit nur einer bewerteten Seite ist keine Neigung, sondern fehlende Daten.
export function buildPowerTilts(
  powers: PowersView | null,
  threshold: number = POWER_STRONG_THRESHOLD,
): PowerTilt[] {
  if (!powers) return [];
  const pullClusters = CLUSTER_ORDER.filter((g) => !PUSH_CLUSTERS.includes(g));
  const axes: Array<{ axis: TiltAxis; a: [TiltSide, PowerGroupKey[]]; b: [TiltSide, PowerGroupKey[]] }> = [
    { axis: "DRIVE", a: ["PUSH", PUSH_CLUSTERS], b: ["PULL", pullClusters] },
    { axis: "MINDSET", a: ["EXPLOIT", EXPLOIT_CLUSTERS], b: ["EXPLORE", EXPLORE_CLUSTERS] },
  ];
  const tilts: PowerTilt[] = [];
  for (const { axis, a, b } of axes) {
    const sideA = strongShare(powers, a[1], threshold);
    const sideB = strongShare(powers, b[1], threshold);
    if (sideA === null || sideB === null) continue;
    const delta = sideA.share - sideB.share;
    const points = Math.abs(delta);
    const standardError = shareDiffStandardError(sideA.strong, sideA.n, sideB.strong, sideB.n);
    const sigma = standardError > 0 ? points / standardError : 0;
    const grade = TILT_GRADE_SIGMA.find(([, min]) => sigma >= min)?.[0] ?? null;
    tilts.push({
      axis,
      side: grade === null ? null : delta > 0 ? a[0] : b[0],
      points,
      standardError,
      grade,
      shares: [sideA.share, sideB.share],
      bothWeak: sideA.share < TILT_WEAK_SHARE && sideB.share < TILT_WEAK_SHARE,
    });
  }
  return tilts;
}
